import {
  completeQueryResult,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  animationFrameScheduler,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  defer,
  filter,
  first,
  from,
  fromEvent,
  map,
  merge,
  of,
  pipe,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  throwError,
  withLatestFrom,
} from 'rxjs';

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ɵcreateInjector as createInjector,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  SchoolNameSource,
  StaticGraphicConfig,
  TabloCustomGraphic,
  TabloElementGroupList,
  TabloFaceToPerson,
  TabloGroupConfig,
  TabloGroupElementConfig,
  TabloGroupMemberType,
  TabloLeadingChildConfigDetails,
  TabloLogoElement,
  TabloMember,
  TabloMemberList,
  TabloPhotoOfSchool,
  TabloPhotoOfSchoolConfig,
  TabloPageBackgroundImageSize,
  TabloPageStructureUpdate,
  TabloProjectPageValidationStatus,
  TabloPageBackgroundConfig,
  TabloProjectStructure,
  TabloProjectValidationReason,
  TabloProjectValidationReasonKind,
  TabloProjectValidationStatus,
  TabloQrConfigHost,
  TabloSinglePage,
  TabloStaticChildPhoto,
  TabloStaticPhotoConfig,
  TabloStaticText,
  TabloStaticTextConfig,
  TabloStructoreElement,
  TabloStructureElementForAdd,
  TabloText,
  TabloTextConfig,
  TabloTextConfigOptions,
} from '@ui/tablo-generator/types';

import { UntypedFormControl } from '@angular/forms';
import {
  firstValueFrom,
  RxjsOperators,
} from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';

import cuid from 'cuid';
import { TypedFormBuilder } from '@ui/form/TypedFormBuilder';
import {
  GetPublicSessionOrganizationSettingsGQL,
  TabloProject,
  UpdateTabloProjectGQL,
  UpdateTabloProjectMutationVariables,
  ZalamoObject,
} from '../../../../../../photographer-panel/src/graphql';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { ImageContainer } from '../../../../../../../graphql/apps/photographer/src/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { FaceStrongDetectionService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-strong-detection.service';
import { TabloType } from '../../../../../../photographer-panel/src/app/modules/designer/graphql';
import {
  PublicSessionOrganizationMemberRole,
  PublicSessionOrganizationSettings,
} from '../../../../../../zalamo-support/src/graphql';
import { FontLoaderService } from '@ui/fonts-display/font-loader.service';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { UploadContext } from '@ui/orders/graphql';
import { SingleFileUploaderComponent } from 'ZalamoLibrary/src/lib/upload/components/single-file-uploader/single-file-uploader.component';
import { SingleFileUploaderNewFileDetails } from 'ZalamoLibrary/src/lib/upload/components/single-file-uploader/types';
import { GetImageUrlService } from 'ZalamoLibrary/src/lib/core/image/image-loader/get-image-url.service';
import {
  ImageMetadataStatic,
  isUnsupportedJpegColorSpaceError,
} from 'ZalamoLibrary/src/lib/core/image/image-metadata.service';
import { FontsManagerService } from '@ui/fonts-manager/fonts-manager.service';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';
import { UploadService } from 'ZalamoLibrary/src/lib/upload/upload.service';
import { GetOrganizationBasicDetailsGQL } from '../../../../../../photographer-panel/src/app/modules/studio-manager/studio-basic-settings/graphql';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Draft, enableMapSet, produce, setAutoFreeze } from 'immer';
import { environment } from '../../../../../../photographer-panel/src/environments/environment';
import { TabloProjectStateSyncService } from '@ui/tablo-generator/tablo-studio/tablo-project-state-sync.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { TabloSidebarNavigationService } from './sidebar/tablo-sidebar-navigation.service';
import { TabloSidebarPanel } from './sidebar/tablo-sidebar.types';
import { createTabloStructureSignature } from './tablo-structure-signature';
import isEqual from 'lodash/isEqual';
import { TabloProjectHistory } from './tablo-project-history';
import { TabloProjectSaveQueue } from './tablo-project-save-queue';
import {
  areTabloPageBackgroundsEqual,
  createDefaultTabloPageBackground,
  normalizeTabloPageBackground,
} from './tablo-background-transform';

enableMapSet(); // if you store Map / Set in state
if (environment.production) {
  setAutoFreeze(false);
}

export function updateState<T>(
  snapshot: T,
  recipe: (draft: Draft<T>) => void,
): T {
  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  // @ts-ignore – `produce` inference is good enough
  return produce(snapshot, recipe);
}

type TabloStudioPage = TabloSinglePage & {
  structure: TabloProjectStructure;
};

type TabloStudioProject = Omit<TabloProject, 'pages' | 'faceMatchConfig'> & {
  pages: TabloStudioPage[];
  faceMatchConfig: Record<string, TabloFaceToPerson>;
  fromHistory?: boolean;
};

interface TabloSaveSnapshot {
  entry: TabloStudioProject;
  variables: UpdateTabloProjectMutationVariables;
  signature: string;
}

type TabloNameSource = {
  name: string;
  type: ZalamoObject;
  officialName?: string;
  shortName?: string;
};

const TABLO_PROJECT_BASE_DPI = 300;
const TABLO_BACKGROUND_MIN_DPI = 200;
const TABLO_BACKGROUND_MIN_PAN_PADDING = 40;

const normalizeTabloPage = (
  page?: Partial<TabloStudioPage> | null,
): TabloStudioPage => ({
  structure: page?.structure ?? [],
  url: page?.url ?? null,
  background: normalizeTabloPageBackground(page?.background),
  backgroundImageSize: page?.backgroundImageSize ?? null,
});

const isGroup = (
  it: TabloStructoreElement,
): it is Extract<TabloStructoreElement, { type: 'group' }> => {
  return it.type === 'group';
};
const randomIntFromInterval = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};
const addRandomEffects = (
  config: TabloGroupConfig,
  tabloMember: TabloMember,
): TabloMember => {
  return {
    ...tabloMember,
    childNameTiltLast: config.childNameTilt,
    childTiltLast: config.childTilt,
    childPositionDistortionLast: config.childPositionDistortion,
    nameTilt: config.childNameTilt
      ? tabloMember.childNameTiltLast === config.childNameTilt &&
        typeof tabloMember.nameTilt === 'number'
        ? tabloMember.nameTilt
        : randomIntFromInterval(-config.childNameTilt, config.childNameTilt)
      : 0,
    childTilt: config.childTilt
      ? tabloMember.childTiltLast === config.childTilt &&
        typeof tabloMember.childTilt === 'number'
        ? tabloMember.childTilt
        : randomIntFromInterval(-config.childTilt, config.childTilt)
      : 0,
    childPositionDistortion: config.childPositionDistortion
      ? tabloMember.childPositionDistortionLast ===
          config.childPositionDistortion &&
        tabloMember.childPositionDistortion &&
        typeof tabloMember.childPositionDistortion !== 'number'
        ? tabloMember.childPositionDistortion
        : {
            x: randomIntFromInterval(
              -config.childPositionDistortion,
              config.childPositionDistortion,
            ),
            y: randomIntFromInterval(
              -config.childPositionDistortion,
              config.childPositionDistortion,
            ),
          }
      : {
          x: 0,
          y: 0,
        },
    childOriginTransform: config.childTilt
      ? tabloMember.childTiltLast === config.childTilt &&
        tabloMember.childOriginTransform
        ? tabloMember.childOriginTransform
        : {
            x: randomIntFromInterval(0, 100),
            y: randomIntFromInterval(0, 100),
          }
      : {
          x: 0,
          y: 0,
        },
  };
};
const switchEditModeToFalse = (pages: TabloStudioPage[]): TabloStudioPage[] =>
  pages.map((page) => ({
    ...page,
    structure: page.structure.map((element) => ({
      ...element,
      editMode: false,
      isOnHover: false,
    })),
  }));

@Component({
  selector: 'lib-tablo-studio',
  templateUrl: './tablo-studio.component.html',
  styleUrls: ['./tablo-studio.component.scss'],
  providers: [TabloSidebarNavigationService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabloStudioComponent
  extends AliveState
  implements OnInit, OnDestroy
{
  get publicSessionOrganizationId(): string {
    return this._publicSessionOrganizationId;
  }

  publicSessionOrganizationId$ = new ReplaySubject<string>(1);
  @Input() set publicSessionOrganizationId(value: string) {
    this._publicSessionOrganizationId = value;
    this.publicSessionOrganizationId$.next(value);
  }
  private updateFromBottom = false;
  private suppressedCanvasEchoesByPage?: Map<number, string>;

  @Input() set members(members: TabloMemberList[]) {
    this.memberLists$.next(members);
  }

  @ViewChild('errorsExplained', { static: true })
  errorsExplained: TemplateRef<any>;
  @ViewChild('canvaContainer', { static: false })
  canvaContainer: ElementRef<HTMLDivElement>;
  photographerLogoFile$ = this.organizationService
    .getCurrentOrganization()
    .pipe(
      first(),
      switchMap((organizationId) => {
        return this.getOrganizationDetails
          .fetch({ variables: { organizationId } })
          .pipe(
            map(
              ({
                data: {
                  getOrganizationConfigDetails: { horizontalLogo, squareLogo },
                },
              }) => ({
                horizontalLogo,
                squareLogo,
              }),
            ),
          );
      }),
    );

  currentStaticPhotoType!: 'child' | 'school';

  @Input() set organizationDetails(details: PublicSessionOrganizationSettings) {
    if (details?.logo) {
      this.logo$.next(details.logo);
    }
  }
  names$ = new BehaviorSubject<TabloNameSource[]>([]);
  @Input() set photoSourceGroups(groups: ImageContainer[]) {
    this.sourceGroups$.next(groups);
  }

  exited = false;
  @Input() set preset(preset: TabloProject) {
    if (!preset || this.exited) {
      return;
    }
    // Apollo broadcasts autosave responses too. The open editor owns its local
    // state; an older response must never replace edits made during the request.
    if (
      preset.tabloProjectId &&
      this.fullProject$.value?.tabloProjectId === preset.tabloProjectId
    ) {
      return;
    }
    const presetParsed: TabloStudioProject = {
      ...preset,
      lastHistoryEntryId: preset.lastHistoryEntryId || cuid(),
      pages: preset.pages.map((page) =>
        normalizeTabloPage(JSON.parse(page)),
      ) as TabloStudioPage[],
      faceMatchConfig: JSON.parse(preset.faceMatchConfig),
    };

    switch (preset.type) {
      case TabloType.TwoPage: {
        for (let i = preset.pages.length; i < 2; i++) {
          presetParsed.pages.push(normalizeTabloPage());
        }
        break;
      }
      case TabloType.SinglePage: {
        for (let i = preset.pages.length; i < 1; i++) {
          presetParsed.pages.push(normalizeTabloPage());
        }
        break;
      }
    }
    const pagesDump = [...presetParsed.pages];
    for (const page of presetParsed.pages) {
      for (const element of page.structure) {
        if (typeof element.config.index === 'undefined') {
          element.config.index = page.structure.indexOf(element);
        }
      }
    }
    const fullStructure = presetParsed.pages
      .flatMap((v) => v.structure)
      .filter(
        (v) =>
          v.type === 'group' &&
          v.config.list.type === TabloGroupMemberType.student,
      ) as TabloGroupElementConfig[];
    const childrenInGroups = [
      ...new Set(
        fullStructure.flatMap((v) =>
          v.config.list.members
            .filter(
              (member) =>
                !v.config.config.notAttendants.includes(member.tabloMemberId),
            )
            .map((v) => v.tabloMemberId),
        ),
      ),
    ];

    if (!this.isTemplateEditor) {
      const childWithoutFace = childrenInGroups.find(
        (child) => !presetParsed.faceMatchConfig[child],
      );
      if (childWithoutFace) {
        this.askForFaceMatch.next(
          fullStructure.find((v) =>
            v.config.list.members.find(
              (member) => member.tabloMemberId === childWithoutFace,
            ),
          ),
        );
      }
    }

    // database
    //   .table('historyEntries')
    //   .where('tabloProjectId')
    //   .equals(preset.tabloProjectId)
    //   .last()
    //   .then((lastEntryFromHistory) => {
    // if (
    //   lastEntryFromHistory?.lastHistoryEntryId >
    //     preset.lastHistoryEntryId ||
    //   (!preset.lastHistoryEntryId &&
    //     lastEntryFromHistory?.lastHistoryEntryId)
    // ) {
    //   this.fullProject$.next(lastEntryFromHistory);
    //   this.templateControl.setValue(lastEntryFromHistory);
    //   this.tabloProjectStateSyncService.setBaseState(lastEntryFromHistory);
    // } else {
    this.fullProject$.next(presetParsed);
    this.templateControl.setValue(presetParsed);
    this.tabloProjectStateSyncService.setBaseState(presetParsed);
    this.lastSavedHistoryEntryId$.next(presetParsed.lastHistoryEntryId ?? null);
    this.lastSavedProjectSignature =
      this.createAutosavePayloadSignature(presetParsed);
    this.lastRecordedProjectSignature = null;
    // Keep the baseline before the first debounced edit so immediate undo works.
    void this.projectHistory.record(presetParsed).catch(() => {
      this.historyError$.next(true);
    });
    console.log(preset);
    // }
    // });
  }

  pushChangesDownstream = new Subject<boolean>();

  @Input() set names(names: TabloNameSource[]) {
    if (!names) {
      return;
    }
    this.names$.next(names);
  }

  memberLists$ = new BehaviorSubject<TabloMemberList[]>([]);
  @ViewChild('backgroundImageUploader', { static: false })
  backgroundImageUploader: SingleFileUploaderComponent;
  uploadContextForAssets = UploadContext.DesignerAsset;
  subEditorType$ = new ReplaySubject<TabloGroupMemberType | ZalamoObject>(1);
  scaleControl = new UntypedFormControl(1);
  minScaleValue = new BehaviorSubject(0);
  maxScaleValue = new BehaviorSubject(1);
  @Input() tabloType: TabloType = TabloType.SinglePage;
  tabloTypes = TabloType;
  @Input() isTemplateEditor = false;
  @Input() @Required context: ImageContainer;
  @Input() @Required contextId: string;
  sourceGroups$ = new BehaviorSubject<ImageContainer[]>([]);
  readonly sidebarState$ = this.sidebarNavigation.state$;
  private _publicSessionOrganizationId: string;
  @Input() publicSessionOrganizationGroupId: string;
  @Input() publicSessionSignupCampaignId: string | null = null;
  logo$ = new BehaviorSubject<string>(null);
  readonly fullProject$ = new BehaviorSubject<TabloStudioProject | null>(null);
  currentGroupChildren: { idx: number; name: string }[] = [];
  private currentChildGroupId: string; // group we are editing now
  private currentChildIdx = 0;
  private groupEditSubscription: Subscription | null = null;
  private singleChildEditSubscription: Subscription | null = null;
  backgroundImage = new UntypedFormControl();
  validationStatus$ = new BehaviorSubject<TabloProjectValidationStatus>(null);
  x$ = new BehaviorSubject(0);
  y$ = new BehaviorSubject(0);

  askForFaceMatch = new Subject<any>();
  currentGroupId = null;
  currentPage$ = new BehaviorSubject(0);
  backgroundImageUpdate = false;

  get isSinglePageTablo() {
    return this.tabloType === TabloType.SinglePage;
  }

  get project() {
    return this.fullProject$.value;
  }
  fullProjectSelfUpdate = false;
  private readonly sidebarTitles: Record<TabloSidebarPanel, string> = {
    main: '',
    layers: 'Dodaj element',
    'group-edit': 'Edycja grupy',
    'text-edit': 'Edycja tekstu',
    'static-text-edit': 'Edycja tekstu',
    'photo-edit': 'Edycja zdjęcia',
    'graphic-edit': 'Edycja grafiki',
    'face-match': 'Dopasowanie twarzy',
    'single-child-edit': 'Pojedyncza osoba',
  };

  getSidebarTitle(panel: TabloSidebarPanel): string {
    return this.sidebarTitles[panel] || '';
  }

  private getProjectWithSyncedDerivedContent(
    project: TabloStudioProject,
    names: TabloNameSource[],
    shouldApplyLeadingChildImagesSource: boolean,
    leadingChildImagesSource: string[],
  ): TabloStudioProject | null {
    let hasChanges = false;
    const pages = project.pages.map((page) => {
      let pageHasChanges = false;
      const structure = page.structure.map((element) => {
        const nextElement = this.getElementWithSyncedDerivedContent(
          element,
          names,
          shouldApplyLeadingChildImagesSource,
          leadingChildImagesSource,
        );
        if (nextElement !== element) {
          pageHasChanges = true;
        }
        return nextElement;
      });

      if (!pageHasChanges) {
        return page;
      }

      hasChanges = true;
      return {
        ...page,
        structure,
      };
    });

    return hasChanges
      ? {
          ...project,
          pages,
        }
      : null;
  }

  private getGroupMemberType(
    group: TabloGroupElementConfig,
  ): TabloGroupMemberType {
    return group.config.list.type === TabloGroupMemberType.leadingChild
      ? TabloGroupMemberType.student
      : group.config.list.type;
  }

  private getSyncedGroupMembers(
    group: TabloGroupElementConfig,
    memberLists: TabloMemberList[],
  ): TabloMember[] | null {
    const matchedList = memberLists.find(
      (memberList) => memberList.type === this.getGroupMemberType(group),
    );

    if (!matchedList) {
      return null;
    }

    const currentMembers = Array.isArray(group.config.list.members)
      ? group.config.list.members
      : [];
    const currentMembersById = new Map(
      currentMembers.map((member) => [member.tabloMemberId, member]),
    );

    return matchedList.members.map((member) => {
      const currentMember = currentMembersById.get(member.tabloMemberId);

      if (!currentMember) {
        return addRandomEffects(group.config.config, member);
      }
      const merged = { ...currentMember, ...member };
      const effectKeys = [
        'childNameTiltLast',
        'childTiltLast',
        'childPositionDistortionLast',
        'nameTilt',
        'childTilt',
        'childPositionDistortion',
        'childOriginTransform',
      ] as const;
      for (const key of effectKeys) {
        if (Object.prototype.hasOwnProperty.call(currentMember, key)) {
          Object.assign(merged, { [key]: currentMember[key] });
        } else {
          // Preserve absence too: adding undefined properties would make an
          // unchanged member look different and rebuild the project on save.
          delete merged[key];
        }
      }
      return merged;
    });
  }

  private areMembersEqual(
    left: readonly TabloMember[],
    right: readonly TabloMember[],
  ): boolean {
    return isEqual(left, right);
  }

  private getProjectWithSyncedMemberLists(
    project: TabloStudioProject,
    memberLists: TabloMemberList[],
  ): TabloStudioProject {
    if (!memberLists.length) {
      return project;
    }

    let hasChanges = false;
    const pages = project.pages.map((page) => {
      let pageHasChanges = false;
      const structure = page.structure.map((element) => {
        if (element.type !== 'group') {
          return element;
        }

        const syncedMembers = this.getSyncedGroupMembers(element, memberLists);
        const currentMembers = Array.isArray(element.config.list.members)
          ? element.config.list.members
          : [];
        if (
          !syncedMembers ||
          this.areMembersEqual(currentMembers, syncedMembers)
        ) {
          return element;
        }

        pageHasChanges = true;
        return {
          ...element,
          config: {
            ...element.config,
            list: {
              ...element.config.list,
              members: syncedMembers,
            },
          },
        };
      });

      if (!pageHasChanges) {
        return page;
      }

      hasChanges = true;
      return {
        ...page,
        structure,
      };
    });

    return hasChanges
      ? {
          ...project,
          pages,
        }
      : project;
  }

  private getGroupEditorConfigForSave(
    config: TabloGroupConfig,
    group: TabloGroupElementConfig,
  ): TabloGroupConfig {
    const attendants = Array.isArray(config.attendants)
      ? [...config.attendants]
      : [];
    const notAttendants = group.config.list.members
      .map((member) => member.tabloMemberId)
      .filter((memberId) => !attendants.includes(memberId));

    return {
      ...config,
      attendants,
      notAttendants,
      addSpine: config.itemsPerRow === 1 ? false : config.addSpine,
    };
  }

  private getGroupMembersWithRandomEffects(
    config: TabloGroupConfig,
    members: readonly TabloMember[],
  ): TabloMember[] {
    return members.map((member) => addRandomEffects(config, member));
  }

  private getGroupEditorUpdate(
    config: TabloGroupConfig,
    group: TabloGroupElementConfig,
  ): { config: TabloGroupConfig; members: TabloMember[] } | null {
    const configForSave = this.getGroupEditorConfigForSave(config, group);
    const membersForSave = this.getGroupMembersWithRandomEffects(
      configForSave,
      group.config.list.members,
    );

    return !isEqual(group.config.config, configForSave) ||
      !isEqual(group.config.list.members, membersForSave)
      ? {
          config: configForSave,
          members: membersForSave,
        }
      : null;
  }

  private getElementWithSyncedDerivedContent(
    element: TabloStructoreElement,
    names: TabloNameSource[],
    shouldApplyLeadingChildImagesSource: boolean,
    leadingChildImagesSource: string[],
  ): TabloStructoreElement {
    if (
      element.type === 'group' &&
      shouldApplyLeadingChildImagesSource &&
      this.shouldSyncGroupImagesSource(element, leadingChildImagesSource)
    ) {
      return {
        ...element,
        config: {
          ...element.config,
          imagesSource: leadingChildImagesSource,
        },
      };
    }

    if (element.type === 'text') {
      return this.getTextElementWithSyncedSourceName(element, names);
    }

    return element;
  }

  private shouldSyncGroupImagesSource(
    element: TabloGroupElementConfig,
    leadingChildImagesSource: string[],
  ): boolean {
    if (!leadingChildImagesSource.length) {
      return false;
    }

    const shouldUseLeadingChildImages =
      element.isLeadingChild ||
      (element.config.list.type === TabloGroupMemberType.student &&
        element.config.imagesSource?.length === 0);

    return (
      shouldUseLeadingChildImages &&
      !this.areStringArraysEqual(
        element.config.imagesSource,
        leadingChildImagesSource,
      )
    );
  }

  private getTextElementWithSyncedSourceName(
    element: TabloText,
    names: TabloNameSource[],
  ): TabloText {
    const sourceName = names.find(
      (name) => name.type === element.config.source,
    );
    const nextText = sourceName?.name;
    const nextOfficialName = sourceName?.officialName;
    const nextShortName = sourceName?.shortName;

    if (
      element.config.config.text === nextText &&
      element.config.officialName === nextOfficialName &&
      element.config.shortName === nextShortName
    ) {
      return element;
    }

    return {
      ...element,
      config: {
        ...element.config,
        config: {
          ...element.config.config,
          text: nextText,
        },
        officialName: nextOfficialName,
        shortName: nextShortName,
      },
    };
  }

  private areStringArraysEqual(
    left: string[] | null | undefined,
    right: string[],
  ): boolean {
    if (!left || left.length !== right.length) {
      return false;
    }

    return left.every((value, index) => value === right[index]);
  }

  private emitSyncedDerivedProject(project: TabloStudioProject): void {
    this.fullProjectSelfUpdate = true;
    try {
      this.fullProject$.next(project);
    } finally {
      this.fullProjectSelfUpdate = false;
    }
  }

  private createAutosavePayloadSignature(entry: TabloStudioProject): string {
    return JSON.stringify({
      tabloProjectId: entry.tabloProjectId,
      pages: switchEditModeToFalse(entry.pages),
      lastHistoryEntryId: entry.lastHistoryEntryId,
      faceMatchConfig: entry.faceMatchConfig,
    });
  }

  projectStructure$ = combineLatest([
    this.fullProject$.pipe(
      RxjsOperators.filterTruthy(),
      filter(() => !this.fullProjectSelfUpdate),
    ),
    this.currentPage$.pipe(
      tap(() => {
        this.patchProject((project) => {
          const structure = project.pages
            .flatMap((p) => p.structure)
            .filter((v) => v.editMode);
          for (const element of structure) {
            element.editMode = false;
          }
        });
        this.resetSidebarToMain(false);
      }),
    ),
    this.sourceGroups$.pipe(RxjsOperators.filterTruthy()),
    this.memberLists$,
    this.names$,
  ]).pipe(
    switchMap(async ([project, page, sourceGroups, members, names]) => {
      this.backgroundImageUpdate = true;
      if (project.pages[page]?.url) {
        this.backgroundImage.setValue(project.pages[page]?.url);
      } else {
        this.backgroundImageUploader?.clear();
      }

      this.backgroundImageUpdate = false;
      const flatStructure = project.pages.flatMap((p) => p.structure);
      const hasLeadingChild = flatStructure.find(
        (item): item is TabloGroupElementConfig =>
          item.type === 'group' &&
          item.isLeadingChild &&
          !this.isTemplateEditor,
      );
      const leadingChildImagesSource =
        !this.isTemplateEditor &&
        hasLeadingChild &&
        hasLeadingChild.config.imagesSource?.length === 0 &&
        !hasLeadingChild.config.config.isNameOnly
          ? sourceGroups.length === 1
            ? [sourceGroups[0].photoGroupId]
            : await this.pickSourcePhotosForCurrentGroup([], flatStructure)
          : [];
      const shouldApplyLeadingChildImagesSource =
        !this.isTemplateEditor &&
        !!hasLeadingChild &&
        hasLeadingChild.config.imagesSource?.length === 0 &&
        !!leadingChildImagesSource.length;
      const syncedProject = this.getProjectWithSyncedDerivedContent(
        project,
        names,
        shouldApplyLeadingChildImagesSource,
        leadingChildImagesSource,
      );
      const projectForStructure = syncedProject ?? project;
      const flatStructureForStructure =
        projectForStructure.pages.reduce<TabloProjectStructure>(
          (structure, currentPage) => [...structure, ...currentPage.structure],
          [],
        );

      if (syncedProject) {
        this.emitSyncedDerivedProject(syncedProject);
      }

      this.validationStatus$.next(this.isValid());

      return projectForStructure.pages[page].structure.map((el) => {
        if (el.type === 'group') {
          // ****************************************
          // 1. figure out what the new members array should look like
          // ****************************************
          const matchedList = this.memberLists$.value.find(
            (m) =>
              m.type ===
              (el.config.list.type === 'leadingChild'
                ? 'student'
                : el.config.list.type),
          );
          const allMembers = members.flatMap((member) =>
            member.members.map((v) => v.tabloMemberId),
          );
          const newMembers = (matchedList?.members ?? [])
            .map((newMember) => {
              const oldMember = el.config.list.members.find(
                (o) => o.tabloMemberId === newMember.tabloMemberId,
              );

              return oldMember
                ? {
                    ...newMember,
                    childNameTiltLast: oldMember.childNameTiltLast,
                    childTiltLast: oldMember.childTiltLast,
                    childPositionDistortionLast:
                      oldMember.childPositionDistortionLast,
                    nameTilt: oldMember.nameTilt,
                    childTilt: oldMember.childTilt,
                    childPositionDistortion: oldMember.childPositionDistortion,
                    childOriginTransform: oldMember.childOriginTransform,
                  }
                : addRandomEffects(el.config.config, newMember);
            })
            .filter((v) => allMembers.includes(v.tabloMemberId));

          // ****************************************
          // 2. return a brand-new element with the new members array
          // ****************************************
          return {
            ...el,
            config: {
              ...el.config,
              leadingChildId: el.isLeadingChild
                ? // @ts-ignore
                  !el.config.leadingChildId ||
                  // @ts-ignore
                  el.config.leadingChildId.length < 10
                  ? newMembers[0]?.tabloMemberId
                  : // @ts-ignore
                    el.config.leadingChildId
                : null,
              list: { ...el.config.list, members: newMembers },
              config: {
                ...el.config.config,
                // initialise leading-child id if necessary
                ...(el.isLeadingChild &&
                (!el.config.config.leadingChildId ||
                  el.config.config.leadingChildId.length < 10)
                  ? { leadingChildId: newMembers[0]?.tabloMemberId }
                  : {}),
              },
              // keep or swap the imagesSource without mutating the original list
              imagesSource:
                el.isLeadingChild &&
                !el.config.imagesSource?.length &&
                !this.isTemplateEditor
                  ? ((
                      flatStructureForStructure.find(
                        (f) =>
                          f.type === 'group' &&
                          f.config.list?.type === 'student',
                      ) as TabloGroupElementConfig
                    )?.config?.imagesSource ?? leadingChildImagesSource)
                  : el.config.imagesSource,
            },
          };
        }

        if (el.type === 'text') {
          const sourceName = this.names$.value.find(
            (n) => n.type === el.config.source,
          );
          return {
            ...el,
            config: {
              ...el.config,
              config: { ...el.config.config, text: sourceName?.name },
              officialName: sourceName?.officialName,
              shortName: sourceName?.shortName,
            },
          };
        }

        // untouched element types:
        return el;
      });
    }),
    tap(() => this.isValid()),
    shareReplay(1),
  );
  projectStructureToPassDown$ = this.projectStructure$.pipe(
    withLatestFrom(
      this.currentPage$,
      this.fullProject$.pipe(RxjsOperators.filterTruthy()),
    ),
    filter(([, page, project]) => {
      const shouldPassChangesDown = !this.consumeCanvasEchoSuppression(
        page,
        project.lastHistoryEntryId,
      );
      console.log('SHOULD PASS CHANGES DOWN: ', shouldPassChangesDown);
      return shouldPassChangesDown;
    }),
    map(([currentStructure]) => currentStructure),
    tap((currentStructure) => console.log({ currentStructure })),
    shareReplay(1),
  );
  leadingChildId$ = this.fullProject$.pipe(
    RxjsOperators.filterTruthy(),
    map((struct) => {
      const element = struct.pages
        .flatMap((v) => v.structure)
        // @ts-ignore
        .find((item) => item.config?.config?.leadingChildId);
      // @ts-ignore
      return element?.config?.config?.leadingChildId;
    }),
  );
  membersMenu$ = combineLatest([
    this.memberLists$,
    this.projectStructure$.pipe(RxjsOperators.filterTruthy()),
  ]).pipe(
    map(([memberLists, structure]) => {
      return memberLists
        .map((memberList) => {
          return {
            ...memberList,
            canAdd: !structure.some(
              (group) =>
                // @ts-ignore
                group.type === 'group' &&
                // @ts-ignore
                group.config.list?.type === memberList.type,
            ),
          };
        })
        .filter((item) => item.canAdd);
    }),
  );

  templateControl = new UntypedFormControl();
  photoSourceControl = new UntypedFormControl();
  currentPhotoSourceControl = new UntypedFormControl([]);
  hideControls = new Subject<boolean>();
  currentTemplate$ = defer(() =>
    this.templateControl.valueChanges.pipe(
      startWith(this.templateControl.value),
      RxjsOperators.filterTruthy(),
    ),
  );

  namesMenu$ = combineLatest([
    this.names$,
    this.projectStructure$.pipe(RxjsOperators.filterTruthy()),
  ]).pipe(
    map(([names, structure]) => {
      return names
        .map((name) => {
          return {
            ...name,
            canAdd: !structure.some(
              (group) =>
                // @ts-ignore
                group.type === 'text' && group.config.source === name.type,
            ),
          };
        })
        .filter((item) => item.canAdd);
    }),
  );

  organizationLogo$ = this.projectStructure$.pipe(
    RxjsOperators.filterTruthy(),
    map((structure) => ({
      name: 'logo',
      canAdd: !structure.some(
        (group) => (group as unknown as TabloLogoElement).type === 'logo',
      ),
    })),
  );
  photographerLogo$ = this.projectStructure$.pipe(
    RxjsOperators.filterTruthy(),
    map((structure) => ({
      name: 'photographerLogo',
      canAdd: !structure.some(
        (group) =>
          (group as unknown as TabloLogoElement).type === 'photographerLogo',
      ),
    })),
  );
  leadingChild$ = this.projectStructure$.pipe(
    RxjsOperators.filterTruthy(),
    map((structure) => ({
      name: 'leadingChild',
      // @ts-ignore
      canAdd: !structure.some(
        (group) =>
          // @ts-ignore
          group.isLeadingChild &&
          ((group.config as unknown as TabloLeadingChildConfigDetails).config
            .isPhotoOnly ||
            (group.config as unknown as TabloLeadingChildConfigDetails).config
              .isNameOnly),
      ),
    })),
  );
  leadingChildPhotoOnly$ = this.projectStructure$.pipe(
    RxjsOperators.filterTruthy(),
    map((structure) => ({
      name: 'leadingChild',
      // @ts-ignore
      canAdd: !structure.some(
        (group) =>
          // @ts-ignore
          group.isLeadingChild &&
          (group.config as unknown as TabloLeadingChildConfigDetails).config
            .isPhotoOnly,
      ),
    })),
  );
  leadingChildNameOnly$ = this.projectStructure$.pipe(
    RxjsOperators.filterTruthy(),
    map((structure) => ({
      name: 'leadingChild',
      // @ts-ignore
      canAdd: !structure.some(
        (group) =>
          // @ts-ignore

          group.isLeadingChild &&
          (group.config as unknown as TabloLeadingChildConfigDetails).config
            .isNameOnly,
      ),
    })),
  );
  currentTextConfig = null;
  currentPhotoConfig = null;
  config = this.typedFormBuilder.typedGroup<TabloGroupConfig>({
    isPhotoOnly: [false],
    isNameOnly: [false],
    leadingChildId: [null],
    childPhotoOverlayFileName: [null],
    childPhotoOverlayFileNameOdd: [null],
    childPhotoOverlayFileNameEven: [null],
    childPhotoOverlayHasColorOverlay: [false],
    childPhotoOverlayHasColorOverlayOdd: [false],
    childPhotoOverlayHasColorOverlayEven: [false],
    childPhotoOverlayColorOverlay: [''],
    childPhotoOverlayColorOverlayOdd: [''],
    childPhotoOverlayColorOverlayEven: [''],
    childPhotoOverlayScale: [1],
    childPhotoOverlayScaleEven: [1],
    childPhotoOverlayScaleOdd: [1],
    hideLeadingChild: [false],
    itemsPerRow: [4],
    notAttendants: [[]],
    attendants: [[]],
    addSpine: [false],
    spineSize: [10],
    align: ['center'],
    sideMargin: [10],
    childVerticalMargin: [10],
    childHorizontalMargin: [10],
    photoAsCircle: [false],
    maximizePhoto: [false],
    nameHandling: ['split'],
    childFirstNameHeight: [20],
    childLastNameHeight: [20],
    childNamesGap: [0],
    font: {
      fontId: null,
      fileName: null,
    },
    fontOdd: {
      fontId: null,
      fileName: null,
    },
    fontEven: {
      fontId: null,
      fileName: null,
    },
    fontLatterSpacingEven: [0],
    fontLatterSpacingOdd: [0],
    fontLatterSpacing: [0],
    photoAsBackground: [false],
    childConfigDifferenceRule: ['none'],
    childFrameWidth: [0],
    childFrameWidthOdd: [0],
    childFrameWidthEven: [0],
    childFrameColor: [''],
    childFrameColorOdd: [''],
    childFrameColorEven: [''],
    photoSideMargins: [0],
    photoSideMarginsOdd: [0],
    photoSideMarginsEven: [0],
    childPhotoFrameWidth: [0],
    childPhotoFrameWidthOdd: [0],
    childPhotoFrameWidthEven: [0],
    childPhotoFrameColor: [''],
    childPhotoFrameColorOdd: [''],
    childPhotoFrameColorEven: [''],
    childLastNameMaxFontSize: [30],
    childFirstNameMaxFontSize: [40],
    childNameBackgroundColor: [null],
    childNameBackgroundColorEven: [null],
    childNameFrameWidth: [0],
    childNameBackgroundColorOdd: [null],
    photoAsSquare: [false],
    childNameBackgroundFile: [null],
    childNameBackgroundFileEven: [null],
    childNameBackgroundFileOdd: [null],

    childNameFrameWidthEven: [0],
    childNameFrameWidthOdd: [0],
    childNameNameFrameColor: [null],
    childNameNameFrameColorEven: [null],
    childNameNameFrameColorOdd: [null],
    childPhotoNameToPhotoGap: [0],
    childPhotoNameFontColor: [''],
    childPhotoNameFontColorEven: [''],
    childPhotoNameFontColorOdd: [''],
    childNameSideMargins: [0],
    childNameSideMarginsEven: [0],
    childNameSideMarginsOdd: [0],
    childPhotoShadow: [null],
    childPhotoShadowOdd: [null],
    childPhotoShadowEven: [null],
    childNameFrameRadiusEven: [0],
    childNameFrameRadiusOdd: [0],
    childNameFrameRadius: [0],
    childBackgroundColor: [null],
    childBackgroundColorEven: [null],
    childBackgroundColorOdd: [null],
    childNameTilt: [0],
    childPositionDistortion: [0],
    childTilt: [0],
    photoRadius: [0],
    photoRadiusEven: [0],
    photoRadiusOdd: [0],
    childShadow: [null],
    childShadowEven: [null],
    childShadowOdd: [null],
    childRadius: [0],
    childRadiusEven: [0],
    childRadiusOdd: [0],
    showDirectors: [true],
    uppercase: [false],
    uppercaseOdd: [false],
    uppercaseEven: [false],
    directorsGoFirst: [false],
  });
  @ViewChild('menuTrigger', { static: true }) menuTrigger!: MatMenuTrigger;
  menuPosition = { x: 0, y: 0 };

  singleChildForm = this.typedFormBuilder.typedGroup<{
    x: number;
    y: number;
    childFrameWidth: number;
    childFrameColor: string;
    photoAsBackground: boolean;
    photoSideMargins: number;
    childPhotoFrameWidth: number;
    childPhotoFrameColor: string;
    childNameFrameWidth: number;
    childNameBackgroundFile?: string;
    childNameFrameRadius: number;
    childNameBackgroundColor: string;
    childNameNameFrameColor: string;
    childPhotoNameToPhotoGap: number;
    childPhotoOverlayFileName: string;
    childPhotoOverlayHasColorOverlay: boolean;
    childPhotoOverlayColorOverlay: string;
    childPhotoNameFontColor: string;
    childNameSideMargins: number;
    childBackgroundColor: string;
    photoRadius: number;
    childShadow: string;
    childRadius: number;
    transform: string;
    nameHandling: 'split' | 'join';
    photoAsCircle: boolean;
    photoAsSquare: boolean;
    childNameTilt: number;
    childTilt: number;
    childPositionDistortion: number;
    childFirstNameMaxFontSize: number;
    childLastNameMaxFontSize: number;
    childFirstNameHeight: number;
    childLastNameHeight: number;
    childIdx: number;
    uppercase: boolean;
  }>({
    /* selektor */
    childIdx: [0],

    /* ───── zdjęcie / geometria ───── */
    x: [0],
    y: [0],
    photoAsBackground: [false],
    photoAsCircle: [false],
    photoAsSquare: [false],
    photoSideMargins: [0],
    photoRadius: [0],
    childPhotoFrameWidth: [0],
    childPhotoFrameColor: [''],
    childPhotoOverlayFileName: [null],
    childPhotoOverlayHasColorOverlay: [false],
    childPhotoOverlayColorOverlay: [''],

    /* ───── ramka / tło osoby ───── */
    childFrameWidth: [0],
    childFrameColor: [''],
    childBackgroundColor: [null],
    childShadow: [null],
    childRadius: [0],

    /* ───── podpis ───── */
    nameHandling: ['join'],
    childFirstNameMaxFontSize: [40],
    childLastNameMaxFontSize: [30],
    childFirstNameHeight: [20],
    childLastNameHeight: [20],
    childPhotoNameToPhotoGap: [0],
    childNameFrameWidth: [0],
    childNameFrameRadius: [0],
    childNameBackgroundFile: [null],
    childNameBackgroundColor: [null],
    childNameNameFrameColor: [null],
    childPhotoNameFontColor: [''],
    childNameSideMargins: [0],
    childNameTilt: [0],
    uppercase: [false],
    /* ───── efekty losowe ───── */
    childTilt: [0],
    childPositionDistortion: [0],
    transform: [''],
  });
  configSelfUpdate = false;
  attendance$ = combineLatest([
    this.config.valueChanges.pipe(
      startWith(this.config.value),
      filter(() => !this.configSelfUpdate),
      tap((x) => console.log('config updated')),
    ),
    this.projectStructure$.pipe(tap((x) => console.log('structure updated'))),
  ]).pipe(
    map(([config, structure]) => {
      const currentGroup = structure.find(
        // @ts-ignore
        (cnf) => cnf.type === 'group' && cnf.id === this.currentGroupId,
      );

      if (!currentGroup) {
        return [];
      }

      this.configSelfUpdate = true;

      // Get the list of all members
      const allMembers = (
        currentGroup.config as unknown as TabloElementGroupList
      ).list.members;

      // Get the list of non-attendants from the current config
      const notAttendants = Array.isArray(
        (currentGroup.config as unknown as TabloElementGroupList).config
          .notAttendants,
      )
        ? (currentGroup.config as unknown as TabloElementGroupList).config
            .notAttendants
        : [];

      // Calculate the list of attendants (members who are not in the notAttendants list)
      // @ts-ignore
      const attendants = currentGroup.config.config.attendants
        ? // @ts-ignore
          [...currentGroup.config.config.attendants]
        : [];
      const newMemberIds = allMembers
        .map((m) => m.tabloMemberId)
        .filter(
          (id) => !attendants.includes(id) && !notAttendants.includes(id),
        );

      // for (const newMemberId of newMemberIds) {
      //   attendants.push(newMemberId);
      // }
      // Only update form controls if values have changed
      if (
        JSON.stringify(this.config.value.attendants) !==
          JSON.stringify(attendants) ||
        JSON.stringify(this.config.value.notAttendants) !==
          JSON.stringify(notAttendants)
      ) {
        this.config.patchValue(
          {
            notAttendants,
            attendants,
          },
          { emitEvent: false },
        ); // Prevent triggering valueChanges
      }

      this.configSelfUpdate = false;
      return (
        currentGroup.config as unknown as TabloElementGroupList
      ).list.members.map((member) => ({
        value: member.tabloMemberId,
        name: `${member.firstName} ${member.lastName}`,
      }));
    }),
    shareReplay(1),
    // Removed first() to allow the observable to emit multiple times
  );
  scaleForEditor = new Subject();
  enableMoveFromCanvas$ = new BehaviorSubject<boolean>(false);
  readonly isSpaceDown$ = new BehaviorSubject<boolean>(false);
  isPreloading$ = this.fullProject$.pipe(
    RxjsOperators.filterTruthy(),
    switchMap((project) =>
      this.organizationService.getCurrentOrganization().pipe(
        switchMap((organizationId) =>
          Promise.all([
            ...project.pages
              .filter((v) => v.url)
              .map(({ url }) =>
                this.getImageUrlService
                  .transform(
                    {
                      photoId: url,
                    },
                    `${this.projectId}-assets`,
                    'photographer',
                    ZalamoObject.DesignerAsset,
                    this.projectId,
                    5000,
                  )
                  .pipe(first())
                  .toPromise(),
              ),
            ...project.pages
              .flatMap((page) => page.structure)
              .filter(
                (v) =>
                  v.type === 'text' &&
                  v.config.config?.font?.fontId &&
                  v.config.config?.font?.fileName,
              )
              .map((v) =>
                this.fontLoaderService
                  .loadFont(
                    (v.config as TabloTextConfig).config.font?.fontId,
                    (v.config as TabloTextConfig).config.font?.fileName,
                    organizationId,
                  )
                  .pipe(first())
                  .toPromise(),
              ),
            ...project.pages
              .flatMap((page) => page.structure)
              .filter(
                (v) =>
                  (v.type === 'group' || v.type === 'leadingChildName') &&
                  ((v.config.config?.font?.fontId &&
                    v.config.config?.font?.fileName) ||
                    v.config.config.fontOdd?.fontId),
              )
              .flatMap((v) => {
                const fonts = [];
                if (
                  (v.config as unknown as TabloElementGroupList).config?.font
                    ?.fontId
                ) {
                  fonts.push({
                    fontId: (v.config as TabloElementGroupList).config.font
                      ?.fontId,
                    fileName: (v.config as TabloElementGroupList).config.font
                      ?.fileName,
                  });
                }
                if (
                  (v.config as unknown as TabloElementGroupList).config.fontOdd
                    ?.fontId
                ) {
                  fonts.push({
                    fontId: (v.config as TabloElementGroupList).config.fontOdd
                      ?.fontId,
                    fileName: (v.config as TabloElementGroupList).config.fontOdd
                      ?.fileName,
                  });
                }
                if (
                  (v.config as unknown as TabloElementGroupList).config.fontEven
                    ?.fontId
                ) {
                  fonts.push({
                    fontId: (v.config as TabloElementGroupList).config.fontEven
                      ?.fontId,
                    fileName: (v.config as TabloElementGroupList).config
                      .fontEven?.fileName,
                  });
                }
                return fonts.map((font) =>
                  this.fontLoaderService
                    .loadFont(font.fontId, font.fileName, organizationId)
                    .pipe(first())
                    .toPromise(),
                );
              }),
            ...project.pages
              .flatMap((page) => page.structure)
              .filter(
                (v) =>
                  v.type === 'statictext' &&
                  ((v.config.config?.font?.fontId &&
                    v.config.config?.font?.fileName) ||
                    (v.config.config?.headerFont?.fontId &&
                      v.config.config?.headerFont?.fileName)),
              )
              .flatMap((v) => {
                const fonts: {
                  config: {
                    config: { font: { fontId: string; fileName: string } };
                  };
                }[] = [];
                if (
                  (v.config as TabloStaticTextConfig).config.font?.fontId &&
                  (v.config as TabloStaticTextConfig).config.font?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloStaticTextConfig).config
                            .font.fileName,
                          fontId: (v.config as TabloStaticTextConfig).config
                            .font.fontId,
                        },
                      },
                    },
                  });
                }
                if (
                  (v.config as TabloStaticTextConfig).config.headerFont
                    ?.fontId &&
                  (v.config as TabloStaticTextConfig).config.headerFont
                    ?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloStaticTextConfig).config
                            .headerFont.fileName,
                          fontId: (v.config as TabloStaticTextConfig).config
                            .headerFont.fontId,
                        },
                      },
                    },
                  });
                }
                return fonts;
              })
              .map((v) =>
                this.fontLoaderService
                  .loadFont(
                    (v.config as TabloTextConfig).config.font?.fontId,
                    (v.config as TabloTextConfig).config.font?.fileName,
                    organizationId,
                  )
                  .pipe(first())
                  .toPromise(),
              ),
          ]),
        ),
      ),
    ),
    map(() => false),
    startWith(true),
    tap((x) => console.log(`PRELOADING: ${x}`)),
  );

  isLeadingChild$ = new ReplaySubject<boolean>(1);
  @Input() projectId!: string;
  saveInProgress$ = new BehaviorSubject(false);
  readonly saveError$ = new BehaviorSubject(false);
  readonly historyError$ = new BehaviorSubject(false);
  private readonly projectHistory =
    new TabloProjectHistory<TabloStudioProject>();
  private projectSaveQueue: TabloProjectSaveQueue<TabloSaveSnapshot> | null =
    null;
  private pendingSaveCount = 0;
  private lastSavedProjectSignature: string | null = null;
  private lastRecordedProjectSignature: string | null = null;
  private lastSavedHistoryEntryId$ = new BehaviorSubject<string | null>(null);
  @Output() exitRequested = new EventEmitter<void>();
  showSpine = new UntypedFormControl(
    localStorage.getItem('tabloEditorShowSpine') === 'true' ||
      localStorage.getItem('tabloEditorShowSpine') === null,
  );
  currentPhotoSourcesPicked = new Subject();
  @ViewChild('pickGroupSourcePhotos', { static: true })
  pickGroupSourcePhotos: TemplateRef<any>;
  pickSourcePhotosPromise = null;
  sourcePhotosPickInProgress = false;
  layers$ = this.projectStructure$.pipe(
    map((v) =>
      v.sort((a, b) => {
        if (a.config.index === b.config.index) {
          return 0;
        } else if (a.config.index > b.config.index) {
          return -1;
        } else {
          return 1;
        }
      }),
    ),
  );
  private readonly destroy$ = new Subject<void>();
  // private static reIndex(struct: TabloProjectStructure): TabloProjectStructure {
  //
  // return struct
  //   .sort((a, b) => (a.config.index ?? 0) - (b.config.index ?? 0))
  //   .map((el, i) => ({ ...el, config: { ...el.config, index: i } }));
  // }
  @ViewChild('pageDump', { static: true })
  pageDump: TemplateRef<any>;

  currentMemberForCrop = null;
  isEditingTeacherGroup$ = new BehaviorSubject(false);
  isRedoAvailable$ = new BehaviorSubject(false);

  currentOrganizationDetails$ = this.publicSessionOrganizationId$.pipe(
    switchMap((publicSessionOrganizationId) =>
      this.getPublicSessionOrganizationSettingsGQL
        .watch({
          variables: {
            publicSessionOrganizationId,
          },
        })
        .valueChanges.pipe(
          throwOnQueryError,
          filter(hasCompleteQueryData),
          map((apolloQueryResult) => {
            const {
              data: { getPublicSessionOrganizationSettings },
            } = completeQueryResult(apolloQueryResult);
            return getPublicSessionOrganizationSettings;
          }),
        ),
    ),
  );
  currentGraphicConfig = null;
  currentRightMenuOptions$ = fromEvent(
    this.elementRef.nativeElement,
    'contextmenu',
  ).pipe(
    switchMap((event: PointerEvent) => {
      console.log(event);
      event.preventDefault();
      const targetId =
        (event.target as HTMLElement)
          .closest('.focusStealer')
          ?.getAttribute('data-id') ||
        (event.target as HTMLElement)
          .closest('.elementContainer')
          ?.getAttribute('id');

      if (!targetId) {
        return of(null);
      }
      this.menuPosition.x = event.clientX;
      this.menuPosition.y = event.clientY;

      // If you open repeatedly at different points, close first.
      this.menuTrigger.closeMenu();

      // Let Angular apply the new left/top before opening.
      setTimeout(() => {
        this.menuTrigger.openMenu();
        // this.menuTrigger.updatePosition();
      });
      return this.projectStructure$.pipe(
        map((v) => v.find((el) => el.id === targetId)),
      );
    }),
    filter((v) => !!v),
    map((item) =>
      [
        !item.config.hidden
          ? {
              label: 'Ukryj',
              action: () => {
                this.toggleElementVisibility(item.id, true);
              },
            }
          : null,
        item.config.locked
          ? {
              label: 'Odblokuj',
              action: () => {
                this.toggleElementLock(item.id, false);
              },
            }
          : {
              label: 'Zablokuj',
              action: () => {
                this.toggleElementLock(item.id, true);
              },
            },
      ].filter((v) => v),
    ),
  );
  constructor(
    private typedFormBuilder: TypedFormBuilder,
    private cd: ChangeDetectorRef,
    private faceStrongDetectionService: FaceStrongDetectionService,
    private fontLoaderService: FontLoaderService,
    private organizationService: OrganizationService,
    private getImageUrlService: GetImageUrlService,
    private updateTabloProjectGQL: UpdateTabloProjectGQL,
    private fontsManagerService: FontsManagerService,
    private zalamoDialogService: ZalamoDialogService,
    private uploadService: UploadService,
    private getOrganizationDetails: GetOrganizationBasicDetailsGQL,
    private ngZone: NgZone,
    private tabloProjectStateSyncService: TabloProjectStateSyncService,
    private elementRef: ElementRef,
    private getPublicSessionOrganizationSettingsGQL: GetPublicSessionOrganizationSettingsGQL,
    private injector: Injector,
    private sidebarNavigation: TabloSidebarNavigationService,
  ) {
    super();
  }

  private patchProject(mutator: (draft: TabloStudioProject) => void): void {
    const currentProject = this.project;
    if (!currentProject) {
      return;
    }
    const nextProject = produce(currentProject, (draft) => {
      // draft.lastHistoryEntryId = cuid(); // bump history on every write
      delete draft.fromHistory;
      mutator(draft); // user‑supplied changes
    });

    if (nextProject !== currentProject) {
      this.fullProject$.next(nextProject);
    }
  }

  /** Patch *only* the page pointed to by `currentPage$` */
  private patchCurrentPage(mutator: (p: TabloStudioPage) => void): void {
    this.patchProject((draft) => mutator(draft.pages[this.currentPage$.value]));
  }

  /** Pure, re‑usable helper for indexing */

  showLayersEditor() {
    this.sidebarNavigation.openRoot('layers');
  }

  closeLayers() {
    this.sidebarNavigation.back();
  }

  sidebarBack(): void {
    switch (this.sidebarNavigation.snapshot.current.panel) {
      case 'layers':
        this.closeLayers();
        break;
      case 'group-edit':
        this.closeGroupEdit();
        break;
      case 'text-edit':
        this.closeTextEdit();
        break;
      case 'static-text-edit':
        this.closeStaticTextEdit();
        break;
      case 'photo-edit':
        this.closePhotoEdit();
        break;
      case 'graphic-edit':
        this.closeGraphicEdit();
        break;
      case 'face-match':
        this.closeFaceMatch();
        break;
      case 'single-child-edit':
        this.closeSingleChildEdit();
        break;
      default:
        break;
    }
  }

  private closeGroupEdit(): void {
    this.groupEditSubscription?.unsubscribe();
    this.groupEditSubscription = null;
    this.singleChildEditSubscription?.unsubscribe();
    this.singleChildEditSubscription = null;
    if (this.currentGroupId) {
      this.patchEditMode(this.currentGroupId, false);
    }
    this.currentGroupId = null;
    this.currentChildGroupId = null;
    this.currentMemberForCrop = null;
    this.singleChildForm.reset();
    this.sidebarNavigation.back();
  }

  private closeTextEdit(): void {
    if (this.currentGroupId) {
      this.patchEditMode(this.currentGroupId, false);
    }
    this.currentGroupId = null;
    this.sidebarNavigation.back();
  }

  private closeStaticTextEdit(): void {
    if (this.currentGroupId) {
      this.patchEditMode(this.currentGroupId, false);
    }
    this.currentGroupId = null;
    this.sidebarNavigation.back();
  }

  private closePhotoEdit(): void {
    if (this.currentGroupId) {
      this.patchEditMode(this.currentGroupId, false);
    }
    this.currentGroupId = null;
    this.sidebarNavigation.back();
  }

  private closeGraphicEdit(): void {
    if (this.currentGroupId) {
      this.patchEditMode(this.currentGroupId, false);
    }
    this.currentGroupId = null;
    this.sidebarNavigation.back();
  }

  private closeSingleChildEdit(): void {
    this.singleChildEditSubscription?.unsubscribe();
    this.singleChildEditSubscription = null;
    this.currentChildGroupId = null;
    this.currentChildIdx = 0;
    this.singleChildForm.reset();
    this.sidebarNavigation.back();
  }

  private resetSidebarToMain(resetProjectEditMode = true): void {
    this.groupEditSubscription?.unsubscribe();
    this.groupEditSubscription = null;
    this.singleChildEditSubscription?.unsubscribe();
    this.singleChildEditSubscription = null;

    if (this.currentGroupId) {
      this.patchEditMode(this.currentGroupId, false);
    }
    this.currentGroupId = null;

    if (this.currentChildGroupId) {
      this.patchEditMode(this.currentChildGroupId, false);
    }
    this.currentChildGroupId = null;
    this.currentMemberForCrop = null;
    this.singleChildForm.reset();

    if (resetProjectEditMode) {
      this.patchProject((project) => {
        const structure = project.pages
          .flatMap((p) => p.structure)
          .filter((v) => v.editMode);
        for (const element of structure) {
          element.editMode = false;
        }
      });
    }

    this.sidebarNavigation.resetToMain();
  }

  patchProperty<K extends keyof TabloSinglePage>(
    prop: K,
    val: TabloSinglePage[K],
  ) {
    this.patchCurrentPage((p) => {
      (p as any)[prop] = val;
    });
    this.templateControl.patchValue({
      ...this.fullProject$.value,
    });
  }

  private updateCurrentPageBackgroundImage(url: string | null): void {
    this.patchCurrentPage((page) => {
      page.url = url;
      page.background = createDefaultTabloPageBackground();
      page.backgroundImageSize = null;
    });
    this.templateControl.patchValue({
      ...this.fullProject$.value,
    });
  }

  private async getBackgroundImageSizeFromFile(
    file: File,
  ): Promise<TabloPageBackgroundImageSize | null> {
    const metadata = await ImageMetadataStatic.getDetails(file);
    const imageDetails = metadata?.file ?? metadata?.pngFile;
    const width = Number(
      typeof imageDetails?.['Image Width'] === 'number'
        ? imageDetails?.['Image Width']
        : imageDetails?.['Image Width']?.value,
    );
    const height = Number(
      typeof imageDetails?.['Image Height'] === 'number'
        ? imageDetails?.['Image Height']
        : imageDetails?.['Image Height']?.value,
    );

    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }

    return {
      width,
      height,
    };
  }

  async backgroundImageFileChanged(
    event: SingleFileUploaderNewFileDetails,
  ): Promise<void> {
    const backgroundImageSize = await this.getBackgroundImageSizeFromFile(
      event.file,
    );

    if (!backgroundImageSize) {
      return;
    }

    let updated = false;
    this.patchProject((project) => {
      project.pages.forEach((page) => {
        if (page.url !== event.fileName) {
          return;
        }

        page.backgroundImageSize = {
          ...backgroundImageSize,
        };
        updated = true;
      });
    });

    if (updated) {
      this.templateControl.patchValue({
        ...this.fullProject$.value,
      });
    }
  }

  private calculateBackgroundDpi(
    page: TabloSinglePage,
    pageWidth: number,
    pageHeight: number,
  ): number | null {
    const imageWidth = Number(page.backgroundImageSize?.width);
    const imageHeight = Number(page.backgroundImageSize?.height);
    const minDimension = Math.min(pageWidth, pageHeight);
    const minimumBackgroundScale =
      minDimension > 0
        ? 1 + (2 * TABLO_BACKGROUND_MIN_PAN_PADDING) / minDimension
        : 1.05;
    const backgroundScale = Math.max(
      page.background?.scale ?? 1,
      minimumBackgroundScale,
      0.0001,
    );

    if (
      !page.url ||
      !Number.isFinite(pageWidth) ||
      !Number.isFinite(pageHeight) ||
      !Number.isFinite(imageWidth) ||
      !Number.isFinite(imageHeight) ||
      pageWidth <= 0 ||
      pageHeight <= 0 ||
      imageWidth <= 0 ||
      imageHeight <= 0
    ) {
      return null;
    }

    const coverScale = Math.max(
      pageWidth / imageWidth,
      pageHeight / imageHeight,
    );
    return TABLO_PROJECT_BASE_DPI / (coverScale * backgroundScale);
  }

  backgroundTransformUpdated($event: {
    page: number;
    background: TabloPageBackgroundConfig;
    syncTemplate?: boolean;
  }): void {
    if (
      !this.fullProject$.value ||
      !Number.isInteger($event.page) ||
      $event.page < 0 ||
      $event.page >= this.fullProject$.value.pages.length
    ) {
      return;
    }

    const page = this.fullProject$.value.pages[$event.page];
    if (areTabloPageBackgroundsEqual(page?.background, $event.background)) {
      return;
    }

    this.patchProject((draft) => {
      draft.pages[$event.page].background = {
        ...$event.background,
      };
    });

    if ($event.syncTemplate) {
      this.templateControl.patchValue({
        ...this.fullProject$.value,
      });
    }
  }

  @HostListener('window:keydown', ['$event'])
  async onKeyDown(e: KeyboardEvent) {
    // CTRL + Z => Undo
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      await this.undo();
    }

    // CTRL + Y => Redo
    // (Sometimes SHIFT+CTRL+Z is also used for Redo)
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      await this.redo();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.groupEditSubscription?.unsubscribe();
    this.singleChildEditSubscription?.unsubscribe();
    this.destroy$.next(undefined);
    this.destroy$.complete();
    this.projectSaveQueue?.destroy();
  }

  enableMoveFromCanvas(enable: boolean): void {
    // You may or may not want this inside the zone;
    // it doesn't matter for a Subject emission.
    console.log({ enable });
    this.enableMoveFromCanvas$.next(enable);
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      let mouseDownForPan = false;
      const panStartPosition = { x: 0, y: 0 };
      const panFinished = new Subject<void>();

      /* ---------- raw DOM streams -------------------------------- */
      const pointerDown$ = fromEvent<PointerEvent>(
        document.body,
        'pointerdown',
        {
          passive: true,
        },
      );
      const pointerMove$ = fromEvent<PointerEvent>(
        document.body,
        'pointermove',
        {
          passive: true,
        },
      );
      const pointerUp$ = fromEvent(document.body, 'pointerup', {
        passive: true,
      });
      const dblClick$ = fromEvent(document.body, 'dblclick', { passive: true });

      /* ---------- reset with double‑click while space is held ----- */
      dblClick$
        .pipe(
          filter(() => this.isSpaceDown$.value),
          takeUntil(this.destroy$),
        )
        .subscribe(() =>
          // re‑enter zone only to update state that the template uses
          this.ngZone.run(() => {
            this.x$.next(0);
            this.y$.next(0);
            this.scaleControl.patchValue(this.minScaleValue.value);
          }),
        );

      /* ---------- drag‑to‑pan ------------------------------------ */
      this.isSpaceDown$
        .pipe(
          filter(Boolean), // space bar must be down
          switchMap(
            () => pointerDown$.pipe(takeUntil(panFinished)), // wait for first click
          ),
          switchMap((start: PointerEvent) => {
            mouseDownForPan = true;
            panStartPosition.x = start.clientX - this.x$.value;
            panStartPosition.y = start.clientY - this.y$.value;

            return pointerMove$.pipe(
              throttleTime(0, animationFrameScheduler), // max 1 / frame
              filter(() => mouseDownForPan),
              tap((move: PointerEvent) =>
                this.ngZone.run(() => {
                  this.x$.next(move.clientX - panStartPosition.x);
                  this.y$.next(move.clientY - panStartPosition.y);
                }),
              ),
              takeUntil(
                merge(
                  panFinished,
                  pointerUp$,
                  this.isSpaceDown$.pipe(filter((down) => !down)),
                ),
              ),
            );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();

      /* ---------- stop pan on pointer‑up or space release -------- */
      merge(pointerUp$, this.isSpaceDown$.pipe(filter((down) => !down)))
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          mouseDownForPan = false;
          panFinished.next(undefined);
        });

      const keyStreams$ = merge(
        fromEvent<KeyboardEvent>(document.body, 'keydown'),
        fromEvent<KeyboardEvent>(document.body, 'keyup'),
        /* keep your extra stream unchanged */
        this.enableMoveFromCanvas$.pipe(
          map(
            (enable) =>
              ({
                code: 'Space',
                type: enable ? 'keydown' : 'keyup',
              }) as KeyboardEvent,
          ),
        ),
      ).pipe(
        tap((x) => console.log(x)),
        filter((ev) => ev.code === 'Space'),
        map((ev) => ev.type === 'keydown'),
        startWith(false),
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntil(this.destroy$),
      );

      /* 2.  When the value actually changes, re‑enter the zone once  */
      keyStreams$.subscribe((isDown) => {
        this.ngZone.run(() => {
          this.isSpaceDown$.next(isDown);
          /* markForCheck if using OnPush templates that bind to it */
          this.cd.markForCheck();
        });
      });
    });
    this.subscribeWhileAlive(
      this.currentPage$.pipe(
        tap((page) => (this.tabloProjectStateSyncService.currentPage = page)),
      ),
      this.showSpine.valueChanges.pipe(
        tap((x) => {
          localStorage.setItem('tabloEditorShowSpine', x);
        }),
      ),
      this.fullProject$.pipe(
        filter((project): project is TabloStudioProject => !!project),
        tap((project) =>
          this.tabloProjectStateSyncService.setBaseState(project),
        ),
      ),
      this.createAutosaveUpdates(),
      this.tabloProjectStateSyncService.stateUpdated().pipe(
        tap((state) => {
          this.fullProject$.next(state);
          this.templateControl.setValue(state);
        }),
      ),
      this.backgroundImage.valueChanges.pipe(
        filter(() => !this.backgroundImageUpdate),
        tap((backgroundImage) => {
          this.updateCurrentPageBackgroundImage(backgroundImage ?? null);
        }),
      ),
      this.faceStrongDetectionService.faceDetected.pipe(
        tap((face) => {
          console.log('FACE TO SAVE');
        }),
      ),
      this.scaleControl.valueChanges.pipe(
        tap((scale) => {
          console.log(1 / (scale / this.minScaleValue.value));
          if (
            Math.max(scale, this.minScaleValue.value) ===
            this.minScaleValue.value
          ) {
            this.x$.next(0);
            this.y$.next(0);
          }
          this.scaleForEditor.next(Math.max(scale, this.minScaleValue.value));
        }),
      ),
      this.photoSourceControl.valueChanges.pipe(
        tap((imagesSource) => {
          const addImagesSource = (project) => ({
            ...project,
            lastHistoryEntryId: cuid(),
            pages: project.pages.map((page, idx) => {
              if (idx !== this.currentPage$.value) {
                return page;
              } // only touch the active page

              return {
                ...page,
                structure: page.structure.map((item) => {
                  const isTargetGroup =
                    item.type === 'group' &&
                    (item.id === this.currentGroupId || // match by plain id …
                      item.config?.list?.id === this.currentGroupId); // …or nested list.id

                  return isTargetGroup
                    ? { ...item, config: { ...item.config, imagesSource } }
                    : item;
                }),
              };
            }),
          });

          // single emission
          this.fullProject$.next(addImagesSource(this.fullProject$.value));
        }),
      ),

      combineLatest([
        this.currentTemplate$,
        this.names$,
        this.memberLists$,
        this.logo$,
      ]).pipe(
        tap(([template, names, groups, logo]) => {
          if (
            logo &&
            this.fullProject$.value.pages
              .flatMap((p) => p.structure)
              // @ts-ignore
              .find((v) => v.type === 'logo')?.config?.fileName !== logo
          ) {
            this.fullProject$.next({
              ...this.fullProject$.value,
              lastHistoryEntryId: cuid(),
              pages: this.fullProject$.value.pages.map((page, index) => {
                return {
                  ...page,
                  structure: page.structure.map((item) => {
                    if (item.type === 'logo') {
                      return {
                        ...item,
                        config: {
                          ...item.config,
                          fileName: logo,
                        },
                      };
                    }
                    return item;
                  }),
                };
              }),
            });

            // this.projectStructure$.next([
            //   ...this.projectStructure$.value,
            //   {
            //     id: cuid(),
            //     type: 'logo',
            //     editMode: false,
            //     config: {
            //       ...template.logo,
            //       fileName: logo,
            //     },
            //   },
            // ]);
          }
        }),
      ),
    );
  }

  get hasLeadingChild() {
    return this.fullProject$.value.pages.reduce((acc, page) => {
      return (
        acc ||
        (page.structure as TabloProjectStructure).some(
          (item) => item.type === 'group' && item.isLeadingChild,
        )
      );
    }, false);
  }

  isValid(
    project: TabloStudioProject = this.fullProject$.value,
  ): TabloProjectValidationStatus {
    /* ─────────────────────────────────────────
     * short aliases (keeps lines readable)
     * ───────────────────────────────────────*/
    type Kind = TabloProjectValidationReasonKind;
    type Reason = TabloProjectValidationReason;

    const faceMatches = project.faceMatchConfig ?? {};
    const pageWidth = Number(project.width);
    const pageHeight = Number(project.height);
    const directorRoles = [
      PublicSessionOrganizationMemberRole.Director,
      PublicSessionOrganizationMemberRole.ViceDirector,
    ];

    /* ─────────────────────────────────────────
     * initialise the result object
     * ───────────────────────────────────────*/
    const result: TabloProjectValidationStatus = {
      isValid: true,
      pages: [],
      global: [],
    };

    type RuntimePhotoMember = TabloMember & {
      photo?: {
        photoId?: string | null;
      };
    };

    const getGroupListType = (
      group: TabloGroupElementConfig,
    ): TabloGroupMemberType =>
      group.config.list.type === TabloGroupMemberType.leadingChild
        ? TabloGroupMemberType.student
        : group.config.list.type;

    const hasAttendantConfig = (group: TabloGroupElementConfig): boolean =>
      Array.isArray(group.config.config.attendants);

    const getAttendantIds = (group: TabloGroupElementConfig): string[] =>
      hasAttendantConfig(group) ? group.config.config.attendants : [];

    const getNotAttendantIds = (group: TabloGroupElementConfig): string[] =>
      Array.isArray(group.config.config.notAttendants)
        ? group.config.config.notAttendants
        : [];

    const getRuntimeMembers = (group: TabloGroupElementConfig): TabloMember[] =>
      this.memberLists$.value.find(
        (list) => list.type === getGroupListType(group),
      )?.members ?? [];

    const getGroupMembers = (group: TabloGroupElementConfig): TabloMember[] => {
      const savedMembers = Array.isArray(group.config.list.members)
        ? group.config.list.members
        : [];

      if (savedMembers.length) {
        return savedMembers;
      }

      const attendantIds = getAttendantIds(group);
      if (!attendantIds.length) {
        return savedMembers;
      }

      const attendantIdSet = new Set(attendantIds);
      return getRuntimeMembers(group).filter((member) =>
        attendantIdSet.has(member.tabloMemberId),
      );
    };

    const getActiveMembers = (
      group: TabloGroupElementConfig,
    ): TabloMember[] => {
      const members = getGroupMembers(group);
      const attendantIds = getAttendantIds(group);

      if (hasAttendantConfig(group)) {
        const attendantIdSet = new Set(attendantIds);
        return members.filter((member) =>
          attendantIdSet.has(member.tabloMemberId),
        );
      }

      const notAttendantIdSet = new Set(getNotAttendantIds(group));
      return members.filter(
        (member) => !notAttendantIdSet.has(member.tabloMemberId),
      );
    };

    const hasSelectedMembersWithoutDetails = (
      group: TabloGroupElementConfig,
    ): boolean =>
      getAttendantIds(group).length > 0 && getGroupMembers(group).length === 0;

    const hasTeacherPhoto = (member: TabloMember): boolean => {
      const runtimeMember = member as RuntimePhotoMember;
      return !!(
        member.photoId ||
        member.avatar ||
        member.x !== undefined ||
        runtimeMember.photo?.photoId
      );
    };

    const isVisibleTeacherMember = (
      group: TabloGroupElementConfig,
      member: TabloMember,
    ): boolean =>
      !!group.config.config.showDirectors ||
      !!member.groupRoles?.length ||
      (member.roles ?? []).some((role) => !directorRoles.includes(role));

    /* ─────────────────────────────────────────
     * iterate through every page
     * ───────────────────────────────────────*/
    project.pages.forEach((page, pageIdx) => {
      const pageStatus: TabloProjectPageValidationStatus = {
        isValid: true,
        reasons: [],
      };

      /** helper that records an error and flips the flags */
      const pushError = (
        type: Kind,
        message: string,
        ids?: { elementId?: string; memberId?: string },
      ) => {
        pageStatus.isValid = false;
        result.isValid = false;
        const reason: Reason = { type, reason: message, ...ids };
        pageStatus.reasons.push(reason);
      };

      /* │► walk every structure element on the page ◄│ */
      (page.structure as TabloProjectStructure)
        .filter((v) => !v.config.hidden)
        .forEach((el) => {
          /* shared font guard */
          const fontIsMissing = (font?: {
            fontId?: string;
            fileName?: string;
          }) => !font?.fontId || !font?.fileName;

          switch (el.type) {
            /* ╭─────────── TEXT ═════════════════════════╗ */
            case 'text': {
              const { font } = (el as TabloText).config.config;
              if (fontIsMissing(font)) {
                pushError('font', `Text "${el.id}" has no font selected.`, {
                  elementId: el.id,
                });
              }
              break;
            }

            /* ╭───────── STATIC TEXT ════════════════════╗ */
            case 'statictext': {
              const { font, headerFont } = (el as TabloStaticText).config
                .config;
              if (fontIsMissing(font)) {
                pushError(
                  'statictextFont',
                  `Static text "${el.id}" is missing body font.`,
                  { elementId: el.id },
                );
              }
              if (fontIsMissing(headerFont)) {
                pushError(
                  'statictextFont',
                  `Static text "${el.id}" is missing header font.`,
                  { elementId: el.id },
                );
              }
              if ((el as TabloStaticText).overflowError) {
                pushError(
                  'statictextOverflow',
                  `Static text "${el.id}" has overflow error.`,
                  { elementId: el.id },
                );
              }
              break;
            }

            /* ╭────────── GROUP ELEMENT ═════════════════╗ */
            case 'group': {
              const group = el as TabloGroupElementConfig;
              const { font, leadingChildId, isPhotoOnly } = group.config.config;

              /* 1 ▸ font present? */
              if (fontIsMissing(font) && !isPhotoOnly) {
                pushError(
                  'groupConfig',
                  `Group "${group.id}" has no font set.`,
                  {
                    elementId: group.id,
                  },
                );
              }

              /* 2 ▸ empty group (all members removed via attendance list) */
              if (!group.isLeadingChild) {
                const hasActiveMembers =
                  getActiveMembers(group).length > 0 ||
                  hasSelectedMembersWithoutDetails(group);
                if (!hasActiveMembers && !this.isTemplateEditor) {
                  pushError(
                    'emptyGroup',
                    `${
                      group.config.list.type === TabloGroupMemberType.student
                        ? 'Uczniowie'
                        : 'Nauczyciele'
                    } mają pustą listę obecności`,
                    { elementId: group.id },
                  );
                }
              }

              /* 3 ▸ photo checks */
              const missing: string[] = [];

              if (group.isLeadingChild) {
                /* a) leading‑child group */
                if (!leadingChildId) {
                  pushError(
                    'groupConfig',
                    `Group "${group.id}" is marked as 'leadingChild' but leadingChildId is empty.`,
                    { elementId: group.id },
                  );
                } else {
                  const hasPhoto =
                    group.config.handPicked?.[leadingChildId]?.photoId ??
                    faceMatches[leadingChildId]?.photoId;

                  if (!hasPhoto) {
                    missing.push(leadingChildId);
                  }
                }
              } else if (
                group.config.list.type !== TabloGroupMemberType.teacher
              ) {
                /* classic group (many members) */
                getActiveMembers(group).forEach((m) => {
                  const hasPhoto =
                    group.config.handPicked?.[m.tabloMemberId]?.photoId ||
                    faceMatches[m.tabloMemberId]?.photoId;
                  if (!hasPhoto) {
                    missing.push(m.tabloMemberId);
                  }
                });
              } else {
                getActiveMembers(group)
                  .filter((m) => isVisibleTeacherMember(group, m))
                  .forEach((m) => {
                    const hasPhoto =
                      group.config.handPicked?.[m.tabloMemberId]?.photoId ||
                      hasTeacherPhoto(m);
                    if (!hasPhoto) {
                      missing.push(m.tabloMemberId);
                    }
                  });
              }

              if (missing.length && !this.isTemplateEditor) {
                pushError(
                  'memberPhoto',
                  `Group "${
                    group.id
                  }" has members without a photo: ${missing.join(', ')}.`,
                  { elementId: group.id },
                );
              }

              break;
            }
            case 'photoOfSchool':
              const photoOfSchool = el as TabloPhotoOfSchool;
              if (
                !photoOfSchool.config.config.fileName &&
                !this.isTemplateEditor
              ) {
                pushError(
                  'missingSchoolPhoto',
                  `Photo of school "${el.id}" has no file selected.`,
                  { elementId: el.id },
                );
              }
              break;
            case 'staticChildPhoto':
              const staticChildPhoto = el as TabloStaticChildPhoto;
              if (
                !staticChildPhoto.config.config.photoId &&
                !this.isTemplateEditor
              ) {
                pushError(
                  'missingChildPhoto',
                  `Static child photo "${el.id}" has no file selected.`,
                  { elementId: el.id },
                );
              }
              break;

            /* ╭──────────── LOGO ═════════════════════════╗ */
            case 'logo':
            case 'photographerLogo': {
              const logo = el as TabloLogoElement;
              if (!logo.config.fileName && !this.isTemplateEditor) {
                pushError(
                  'logo',
                  `${el.type === 'logo' ? 'School' : 'Photographer'} logo "${
                    logo.id
                  }" has no file selected.`,
                  { elementId: logo.id },
                );
              }
              break;
            }

            /* ╭───────── other elements (currently none) ═╗ */
            default:
              // Extend with additional cases as new validation rules appear.
              break;
          }
        });

      const backgroundDpi = this.calculateBackgroundDpi(
        page,
        pageWidth,
        pageHeight,
      );
      if (backgroundDpi !== null && backgroundDpi < TABLO_BACKGROUND_MIN_DPI) {
        pushError(
          'backgroundDpi',
          `Tło ma ${Math.round(
            backgroundDpi,
          )} DPI. Minimalna wartość to ${TABLO_BACKGROUND_MIN_DPI} DPI.`,
        );
      }

      result.pages.push(pageStatus);
    });
    if (
      project.zalamoPrintSizeId &&
      !this.isSinglePageTablo &&
      !project.pages.flatMap((p) => p.structure).some((v) => v.type === 'qr')
    ) {
      result.global.push({
        reason: 'Brakuje miejsca na kod qr',
        type: 'missingQR',
        memberId: null,
      });
      result.isValid = false;
    }

    return result;
  }

  async addText(text) {
    const pickedFont = await this.fontsManagerService.pickFont(
      'Wybierz czcionkę dla tego pola',
    );

    if (!pickedFont) {
      return;
    }
    this.addToCurrentPage({
      type: 'text',
      config: {
        index: null,
        y: 500,
        x: 800,
        transform: 'translate(500px,500px)',
        source: text.type,
        width: 700,
        height: 300,
        officialName: text.officialName,
        shortName: text.shortName,
        config: {
          text: text.name,
          fontSize: 150,
          align: 'left',
          verticalAlign: 'top',
          fontLatterSpacing: 0,
          pickedNameSource: SchoolNameSource.Classic,
          font: {
            fontId: pickedFont.fontId,
            fileName: pickedFont.fileName,
          },
        },
      },
    } as TabloStructureElementForAdd<TabloText>);
  }

  editTextGroup(group) {
    const text = this.fullProject$.value.pages[
      this.currentPage$.value
    ].structure.find((v: TabloText) => v.config.source === group.type);

    this.editText(text, text.id);
  }

  editText(group, groupId) {
    this.currentTextConfig = group.config;
    this.currentGroupId = groupId;
    this.subEditorType$.next(group.type);
    this.patchEditMode(group.id, true);
    this.sidebarNavigation.openRoot('text-edit', {
      structureId: group.id,
    });
  }

  async addLogo() {
    const currentLogo = await firstValueFrom(this.logo$);
    this.addToCurrentPage({
      type: 'logo',
      config: {
        y: 500,
        x: 500,
        transform: 'translate(500px, 500px)',
        fileName: currentLogo,
        height: Math.round(this.fullProject$.value.height * 0.15),
        width: Math.round(this.fullProject$.value.height * 0.15),
        index: null,
      },
    } as TabloStructureElementForAdd<TabloLogoElement>);
  }

  async addMembersGroup(group) {
    const pickedFont = await this.fontsManagerService.pickFont(
      'Wybierz czcionkę dla tego pola',
    );

    if (!pickedFont) {
      return;
    }
    const imagesSource = [];
    if (!this.isTemplateEditor) {
      const imageSources = await firstValueFrom(this.sourceGroups$);
      if (imageSources.length === 1) {
        imagesSource.push(imageSources[0].photoGroupId);
      }
    }
    const height = ['teacher'].includes(group.type)
      ? Math.round(this.fullProject$.value.height / 2)
      : Math.round(this.fullProject$.value.height * 0.75);
    const initChildTextHeight = this.isTemplateEditor
      ? Math.round(height / 30)
      : Math.round(height / 10);
    this.addToCurrentPage({
      type: 'group',
      config: {
        y: 500,
        x: 500,
        index: null,
        transform: 'translate(500px, 500px)',
        list: group,
        id: cuid(),
        height,
        width: ['teacher'].includes(group.type)
          ? Math.round(this.fullProject$.value.width / 10)
          : Math.round(this.fullProject$.value.width * 0.75),
        placeholders: [],
        imagesSource,
        config: {
          notAttendants: this.isTemplateEditor
            ? ['student'].includes(group.type)
              ? [
                  '25',
                  '26',
                  '27',
                  '28',
                  '29',
                  '30',
                  '31',
                  '32',
                  '33',
                  '34',
                  '35',
                  '36',
                  '37',
                  '38',
                  '39',
                  '40',
                ]
              : ['102', '103']
            : [],
          itemsPerRow: ['teacher'].includes(group.type) ? 1 : 6,
          addSpine: this.tabloType === 'TwoPage' && group.type === 'student',
          spineSize: 50,
          align: 'center',
          sideMargin: 10,
          childVerticalMargin: 10,
          childHorizontalMargin: 10,
          photoAsCircle: false,
          maximizePhoto: false,
          nameHandling: 'join',
          childFirstNameHeight: initChildTextHeight,
          childLastNameHeight: initChildTextHeight,
          childNamesGap: 0,
          font: {
            fontId: pickedFont.fontId,
            fileName: pickedFont.fileName,
          },
          childConfigDifferenceRule: 'none',
          childFrameWidth: 0,
          childFrameWidthOdd: 0,
          childFrameWidthEven: 0,
          childFrameColor: 'rgb(255, 255, 255)',
          childFrameColorOdd: 'rgb(255, 255, 255)',
          childFrameColorEven: 'rgb(255, 255, 255)',
          photoSideMargins: 0,
          photoSideMarginsOdd: 0,
          photoSideMarginsEven: 0,
          childPhotoFrameWidth: 0,
          childPhotoFrameWidthOdd: 0,
          childPhotoFrameWidthEven: 0,
          childPhotoFrameColor: 'rgb(255, 255, 255)',
          childPhotoFrameColorOdd: 'rgb(255, 255, 255)',
          childPhotoFrameColorEven: 'rgb(255, 255, 255)',
          childLastNameMaxFontSize: initChildTextHeight,
          childFirstNameMaxFontSize: initChildTextHeight,
          childNameBackgroundColor: 'rgb(255, 255, 255)',
          childNameBackgroundColorEven: 'rgb(255, 255, 255)',
          childNameBackgroundColorOdd: 'rgb(255, 255, 255)',
          childNameFrameWidth: 0,

          childNameFrameWidthEven: 0,
          childNameFrameWidthOdd: 0,
          childNameNameFrameColor: 'rgb(255, 255, 255)',
          childNameNameFrameColorEven: 'rgb(255, 255, 255)',
          childNameNameFrameColorOdd: 'rgb(255, 255, 255)',
          childPhotoNameToPhotoGap: 0,
          childPhotoNameFontColor: 'rgb(0, 0, 0)',
          childPhotoNameFontColorEven: 'rgb(0, 0, 0)',
          childPhotoNameFontColorOdd: 'rgb(0, 0, 0)',
          childNameSideMargins: 0,
          childNameSideMarginsEven: 0,
          childNameSideMarginsOdd: 0,

          childNameFrameRadiusEven: 0,
          childNameFrameRadiusOdd: 0,
          childNameFrameRadius: 0,
          childBackgroundColor: 'rgb(255, 255, 255)',
          childBackgroundColorEven: 'rgb(255, 255, 255)',
          childBackgroundColorOdd: 'rgb(255, 255, 255)',
          childNameTilt: 0,
          childPositionDistortion: 0,
          childTilt: 0,
          photoRadius: 0,
          photoRadiusEven: 0,
          photoRadiusOdd: 0,
          childShadow: null,
          childShadowEven: null,
          childShadowOdd: null,
          childRadius: 0,
          childRadiusEven: 0,
          childRadiusOdd: 0,
        },
      },
    } as TabloStructureElementForAdd<TabloGroupElementConfig>);
  }

  patchEditMode(id: string, edit: boolean) {
    this.patchCurrentPage((page) => {
      const el = page.structure.find((e) => e.id === id);
      if (el) {
        el.editMode = edit;
        if (!edit) {
          el.isOnHover = false;
        }
      }
    });
  }

  async editGroup(group, itemId) {
    const currentStructure = await this.projectStructure$
      .pipe(first())
      .toPromise();
    const currentStructureElement = currentStructure.find(
      (item) =>
        // @ts-ignore
        item.id === itemId,
    ) as unknown as TabloGroupElementConfig;
    const structureId = currentStructureElement.id;
    this.currentGroupId = currentStructureElement.id;
    this.subEditorType$.next(group.type);
    this.isLeadingChild$.next(group.isLeadingChild);
    this.patchEditMode(structureId, true);
    this.sidebarNavigation.openRoot('group-edit', {
      structureId,
      groupId: group.id,
    });
    this.configSelfUpdate = true;
    this.isEditingTeacherGroup$.next(
      group.type === 'teacher' || group.config?.list.type === 'teacher',
    );
    const currentElement = currentStructureElement?.config;
    const currentConfig = currentElement.config;
    const groupsCount = await firstValueFrom(this.sourceGroups$);
    this.photoSourceControl.patchValue(
      groupsCount.length === 1
        ? groupsCount.map((v) => v.photoGroupId)
        : currentStructureElement.config.imagesSource,
    );
    this.config.reset();
    if (
      !this.isTemplateEditor &&
      (currentElement as unknown as TabloElementGroupList).list
    ) {
      // Get all members
      const allMembers = (currentElement as unknown as TabloElementGroupList)
        .list.members;

      // Get the list of non-attendants from the current config
      const notAttendants = Array.isArray(currentConfig.notAttendants)
        ? currentConfig.notAttendants
        : [];

      // Calculate the list of attendants (members who are not in the notAttendants list)
      const attendants = allMembers
        .filter((member) => !notAttendants.includes(member.tabloMemberId))
        .map((member) => member.tabloMemberId);

      this.config.patchValue({
        ...currentConfig,
        childPhotoOverlayScale: currentConfig.childPhotoOverlayScale ?? 1,
        childPhotoOverlayScaleEven:
          currentConfig.childPhotoOverlayScaleEven ?? 1,
        childPhotoOverlayScaleOdd: currentConfig.childPhotoOverlayScaleOdd ?? 1,
        notAttendants: notAttendants,
        attendants: attendants,
      });
    } else {
      this.config.patchValue({
        ...currentConfig,
      });
    }
    this.configSelfUpdate = false;
    this.groupEditSubscription?.unsubscribe();
    this.groupEditSubscription = this.config.valueChanges
      .pipe(
        filter(() => !this.configSelfUpdate),
        tap((config) => {
          const currentProject = this.fullProject$.value;
          const page = currentProject?.pages[this.currentPage$.value];
          const currentGroup = page?.structure.find(
            (element): element is TabloGroupElementConfig =>
              element.type === 'group' && element.config.list.id === group.id,
          );

          if (!currentProject || !currentGroup) {
            return;
          }

          const update = this.getGroupEditorUpdate(config, currentGroup);
          if (!update) {
            return;
          }

          this.pushChangesDownstream.next(true);
          this.fullProject$.next(
            updateState(currentProject, (draft) => {
              draft.lastHistoryEntryId = cuid(); // ➊ new history entry

              const page = draft.pages[this.currentPage$.value]; // ➋ current page only

              // walk through the structure and patch just the matching group
              for (const element of page.structure) {
                if (element.type !== 'group') {
                  continue;
                }
                if (element.config.list.id !== group.id) {
                  continue;
                }

                element.config.list.members = update.members;
                element.config.config = update.config;

                break; // we found and updated the target group, exit loop
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  openEditMode(item: TabloStructoreElement) {
    switch (item.type) {
      case 'text':
        this.editText(item, item.id);
        break;
      case 'group':
        void this.editGroup(item.config.list, item.id);
        break;
      case 'statictext':
        this.editStaticText(item, item.id);
        break;
      case 'photoOfSchool':
      case 'staticChildPhoto':
        this.editStaticPhoto(item);
        break;
      case 'graphic':
      case 'logo':
      case 'photographerLogo':
        this.editStaticGraphic(item);
        break;
    }
  }

  editStaticText(item: TabloStructoreElement, id: string) {
    this.currentTextConfig = item.config;
    this.currentGroupId = id;
    this.patchEditMode(id, true);
    this.sidebarNavigation.openRoot('static-text-edit', {
      structureId: id,
    });
  }

  minScaleSet(scale) {
    const rounded = scale;
    this.minScaleValue.next(rounded);
    this.scaleControl.patchValue(rounded);
  }

  staticTextUpdated(patch: TabloTextConfigOptions): void {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // 1️⃣ history

        const currentPage = draft.pages[this.currentPage$.value];

        const element = currentPage.structure.find(
          (el) => el.id === this.currentGroupId && el.type === 'statictext',
        );

        if (element) {
          (element.config as TabloStaticTextConfig).config = {
            ...(element.config as TabloStaticTextConfig).config,
            ...patch, // merge update
          };
        }
      }),
    );
  }
  staticPhotoUpdated(patch: TabloStaticPhotoConfig): void {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // 1️⃣ history

        const currentPage = draft.pages[this.currentPage$.value];

        const element = currentPage.structure.find(
          (el) => el.id === this.currentGroupId,
        );

        if (element) {
          (element.config as TabloPhotoOfSchoolConfig).config = {
            ...(element.config as TabloPhotoOfSchoolConfig).config,
            ...patch, // merge update
          };
        }
      }),
    );
  }
  async textUpdated($event: TabloTextConfigOptions) {
    const organizationId = await firstValueFrom(
      this.organizationService.getCurrentOrganization(),
    );
    await firstValueFrom(
      this.fontLoaderService.loadFont(
        $event.font.fontId,
        $event.font.fileName,
        organizationId,
      ),
    );

    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // history entry

        const currentPage = draft.pages[this.currentPage$.value];
        const textElement = currentPage.structure.find(
          (el) => el.id === this.currentGroupId && el.type === 'text',
        );

        if (textElement) {
          // Immer lets us “mutate” directly
          Object.assign(
            (textElement.config as TabloTextConfig).config,
            $event, // apply changes
          );
        }
      }),
    );
  }

  private getOrCreateSuppressedCanvasEchoesByPage(): Map<number, string> {
    if (!this.suppressedCanvasEchoesByPage) {
      this.suppressedCanvasEchoesByPage = new Map<number, string>();
    }

    return this.suppressedCanvasEchoesByPage;
  }

  private suppressNextCanvasEcho(page: number, historyEntryId: string): void {
    this.getOrCreateSuppressedCanvasEchoesByPage().set(page, historyEntryId);
  }

  private consumeCanvasEchoSuppression(
    page: number,
    historyEntryId?: string | null,
  ): boolean {
    if (!historyEntryId) {
      return false;
    }

    const suppressedCanvasEchoesByPage =
      this.getOrCreateSuppressedCanvasEchoesByPage();
    if (suppressedCanvasEchoesByPage.get(page) !== historyEntryId) {
      return false;
    }

    suppressedCanvasEchoesByPage.delete(page);
    return true;
  }

  private hasSameStructureAsCurrentPage(
    page: number,
    structure: TabloProjectStructure,
  ): boolean {
    const currentStructure = this.fullProject$.value?.pages[page]?.structure;

    return (
      !!currentStructure &&
      createTabloStructureSignature(currentStructure) ===
        createTabloStructureSignature(structure)
    );
  }

  structureUpdatedFromCanva(updated: TabloPageStructureUpdate): void {
    if (
      !this.fullProject$.value ||
      !updated ||
      !Array.isArray(updated.structure) ||
      !Number.isInteger(updated.page) ||
      updated.page < 0 ||
      updated.page >= this.fullProject$.value.pages.length
    ) {
      return;
    }

    if (this.hasSameStructureAsCurrentPage(updated.page, updated.structure)) {
      return;
    }

    const historyEntryId = cuid();
    this.suppressNextCanvasEcho(updated.page, historyEntryId);
    this.updateFromBottom = true;

    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = historyEntryId; // new history id
        draft.pages[updated.page].structure = reIndexStructure(
          updated.structure,
        ); // replace page
      }),
    );

    this.updateFromBottom = false;
  }

  async pickSourcePhotosForCurrentGroup(
    existingSources: string[] = [],
    projectStructure?: TabloProjectStructure,
  ): Promise<string[]> {
    if (this.sourcePhotosPickInProgress) {
      return await this.pickSourcePhotosPromise;
    }
    const currentGroups = await firstValueFrom(this.sourceGroups$);
    if (currentGroups.length === 1) {
      return [currentGroups[0].photoGroupId];
    }

    // Find existing sources in the project if none are provided
    if (!existingSources || existingSources.length === 0) {
      const currentStructure =
        projectStructure ?? (await firstValueFrom(this.projectStructure$));
      const groupElements = currentStructure.filter(
        (el) => el.type === 'group',
      );

      // Look for any group element with non-empty imagesSource
      for (const element of groupElements) {
        if (
          (element.config as unknown as TabloElementGroupList).imagesSource &&
          (element.config as unknown as TabloElementGroupList).imagesSource
            .length > 0
        ) {
          existingSources = (element.config as unknown as TabloElementGroupList)
            .imagesSource;
          break;
        }
      }
    }

    // Set the form control value to the existing sources
    this.currentPhotoSourceControl.setValue(existingSources);

    this.sourcePhotosPickInProgress = true;
    const ref = this.zalamoDialogService.openFull(this.pickGroupSourcePhotos);

    this.pickSourcePhotosPromise = await firstValueFrom(
      this.currentPhotoSourcesPicked.pipe(
        filter((v) => v !== null),
        tap(() => {
          this.currentPhotoSourceControl.setValue([]);
          ref.close();
        }),
      ),
    );
    this.sourcePhotosPickInProgress = false;
    return this.pickSourcePhotosPromise;
  }

  async handleSourcePhotoPicker(event: {
    item: any;
    callback: (sources: string[]) => void;
  }) {
    // Use the existing pickSourcePhotosForCurrentGroup method to show the picker dialog
    const existingSources = event.item.config.imagesSource || [];
    const selectedSources =
      await this.pickSourcePhotosForCurrentGroup(existingSources);

    // Call the callback with the selected sources
    if (selectedSources && selectedSources.length > 0) {
      event.callback(selectedSources);
      this.fullProject$.next(
        updateState(this.fullProject$.value, (draft) => {
          draft.lastHistoryEntryId = cuid();
          const group = draft.pages
            .flatMap((v) => v.structure)
            .find(
              (el): el is TabloGroupElementConfig => el.id === event.item.id,
            );
          if (group) {
            group.config.imagesSource = selectedSources;
          }
        }),
      );
    }
  }

  async addLeadingChild(isNameOnly = false, isPhotoOnly = false) {
    const allMembers = await this.memberLists$.pipe(first()).toPromise();
    let pickedFont = { fontId: null, fileName: null };
    if (!isPhotoOnly) {
      pickedFont = await this.fontsManagerService.pickFont(
        'Wybierz czcionkę dla tego pola',
      );

      if (!pickedFont) {
        return;
      }
    }
    const imagesSource =
      this.isTemplateEditor || isNameOnly
        ? []
        : await this.pickSourcePhotosForCurrentGroup();
    this.addToCurrentPage({
      type: 'group',
      isLeadingChild: true,
      config: {
        imagesSource,

        y: Math.round(this.fullProject$.value.height * 0.225),
        x: Math.round(this.fullProject$.value.width * 0.6),
        transform: `translate(${Math.round(
          this.fullProject$.value.height * 0.225,
        )}px, ${Math.round(this.fullProject$.value.width * 0.26)}px)`,
        index: null,
        handPicked: {},
        rotation: 0,
        list: {
          type: TabloGroupMemberType.leadingChild,
          members: allMembers.find(
            (v) => v.type === TabloGroupMemberType.student,
          ).members,
          id: cuid(),
          label: 'Leading Child',
        },
        id: cuid(),
        height: this.fullProject$.value.height * (isNameOnly ? 0.05 : 0.75),
        width: this.fullProject$.value.width * 0.3,
        placeholders: [],
        config: {
          isNameOnly,
          isPhotoOnly,
          leadingChildId: allMembers.find(
            (v) => v.type === TabloGroupMemberType.student,
          ).members[0].tabloMemberId,
          notAttendants: [],
          itemsPerRow: 6,
          addSpine: false,
          spineSize: 10,
          align: 'center',
          sideMargin: 0,
          childVerticalMargin: 0,
          childHorizontalMargin: 0,
          photoAsCircle: false,
          maximizePhoto: false,
          nameHandling: 'join',
          childFirstNameHeight: 40,
          childLastNameHeight: 40,
          childNamesGap: 0,
          font: {
            fontId: pickedFont.fontId,
            fileName: pickedFont.fileName,
          },
          childConfigDifferenceRule: 'none',
          childFrameWidth: 0,
          childFrameWidthOdd: 0,
          childFrameWidthEven: 0,
          childFrameColor: 'rgb(255, 255, 255)',
          childFrameColorOdd: 'rgb(255, 255, 255)',
          childFrameColorEven: 'rgb(255, 255, 255)',
          photoSideMargins: 0,
          photoSideMarginsOdd: 0,
          photoSideMarginsEven: 0,
          childPhotoFrameWidth: 0,
          childPhotoFrameWidthOdd: 0,
          childPhotoFrameWidthEven: 0,
          childPhotoFrameColor: 'rgb(255, 255, 255)',
          childPhotoFrameColorOdd: 'rgb(255, 255, 255)',
          childPhotoFrameColorEven: 'rgb(255, 255, 255)',
          childLastNameMaxFontSize: 30,
          childFirstNameMaxFontSize: 40,
          childNameBackgroundColor: 'rgb(255, 255, 255)',
          childNameBackgroundColorEven: 'rgb(255, 255, 255)',
          childNameBackgroundColorOdd: 'rgb(255, 255, 255)',
          childNameFrameWidth: 0,
          childPhotoOverlayFileName: null,
          childPhotoOverlayFileNameEven: null,
          childPhotoOverlayFileNameOdd: null,
          childNameFrameWidthEven: 0,
          childNameFrameWidthOdd: 0,
          childNameNameFrameColor: 'rgb(255, 255, 255)',
          childNameNameFrameColorEven: 'rgb(255, 255, 255)',
          childNameNameFrameColorOdd: 'rgb(255, 255, 255)',
          childPhotoNameToPhotoGap: 0,
          childPhotoNameFontColor: 'rgb(0, 0, 0)',
          childPhotoNameFontColorEven: 'rgb(0, 0, 0)',
          childPhotoNameFontColorOdd: 'rgb(0, 0, 0)',
          childNameSideMargins: 0,
          childNameSideMarginsEven: 0,
          childNameSideMarginsOdd: 0,

          childNameFrameRadiusEven: 0,
          childNameFrameRadiusOdd: 0,
          childNameFrameRadius: 0,
          childBackgroundColor: isNameOnly ? null : 'rgb(255, 255, 255)',
          childBackgroundColorEven: 'rgb(255, 255, 255)',
          childBackgroundColorOdd: 'rgb(255, 255, 255)',
          childNameTilt: 0,
          childPositionDistortion: 0,
          childTilt: 0,
          photoRadius: 0,
          photoRadiusEven: 0,
          photoRadiusOdd: 0,
          childShadow: null,
          childShadowEven: null,
          childShadowOdd: null,
          childRadius: 0,
          childRadiusEven: 0,
          childRadiusOdd: 0,
        },
      },
    } as TabloStructureElementForAdd<TabloGroupElementConfig>);
  }

  private prepareProjectForPersistence(
    entry: TabloStudioProject,
  ): TabloStudioProject {
    const syncedEntry = this.getProjectWithSyncedMemberLists(
      entry,
      this.memberLists$.value,
    );
    if (syncedEntry !== entry && this.fullProject$.value === entry) {
      this.emitSyncedDerivedProject(syncedEntry);
    }
    return { ...syncedEntry, pages: switchEditModeToFalse(syncedEntry.pages) };
  }

  private createAutosaveUpdates(): Observable<unknown> {
    return this.fullProject$.pipe(
      filter(
        (entry): entry is TabloStudioProject => !!entry?.lastHistoryEntryId,
      ),
      // Coalesce slider/typing events before synchronizing, serializing or
      // touching IndexedDB. Discrete edits can still create separate undo steps.
      debounceTime(200),
      concatMap((sourceEntry) =>
        defer(() => {
          const entry = this.prepareProjectForPersistence(sourceEntry);
          const signature = this.createAutosavePayloadSignature(entry);
          if (signature === this.lastRecordedProjectSignature) {
            return EMPTY;
          }
          return from(this.projectHistory.record(entry)).pipe(
            tap(() => {
              this.lastRecordedProjectSignature = signature;
              this.historyError$.next(false);
            }),
            // A local history failure must not disable saving to the server.
            catchError(() => {
              this.historyError$.next(true);
              return of(undefined);
            }),
          );
        }).pipe(
          catchError(() => {
            this.saveError$.next(true);
            return EMPTY;
          }),
        ),
      ),
      debounceTime(800),
      // Read the latest project when executing, not a snapshot delayed by
      // history I/O. Both this path and the manual button use the same queue.
      concatMap(() =>
        defer(() => this.saveProject()).pipe(catchError(() => EMPTY)),
      ),
    );
  }

  private createSaveSnapshot(entry: TabloStudioProject): TabloSaveSnapshot {
    const prepared = this.prepareProjectForPersistence(entry);
    const elements = prepared.pages.flatMap((page) => page.structure);
    const leadingChild = elements.find((element) => {
      const config = element.config.config;
      return config && 'leadingChildId' in config && config.leadingChildId;
    });
    const leadingChildConfig = leadingChild?.config.config;
    return {
      entry: prepared,
      signature: this.createAutosavePayloadSignature(prepared),
      variables: {
        tabloProjectId: prepared.tabloProjectId,
        input: {
          pages: prepared.pages.map((page) => JSON.stringify(page)),
          lastHistoryEntryId: prepared.lastHistoryEntryId,
          isValid: this.isValid(prepared).isValid,
          hasLeadingChild: elements.some(
            (element) => element.type === 'group' && element.isLeadingChild,
          ),
          leadingChildId:
            leadingChildConfig && 'leadingChildId' in leadingChildConfig
              ? leadingChildConfig.leadingChildId
              : null,
          faceMatchConfig: JSON.stringify(prepared.faceMatchConfig),
        },
      },
    };
  }

  private persistProject(snapshot: TabloSaveSnapshot): Observable<unknown> {
    return defer(() =>
      snapshot.signature === this.lastRecordedProjectSignature
        ? of(undefined)
        : from(this.projectHistory.record(snapshot.entry)),
    ).pipe(
      tap(() => {
        this.lastRecordedProjectSignature = snapshot.signature;
        this.historyError$.next(false);
      }),
      catchError(() => {
        this.historyError$.next(true);
        return of(undefined);
      }),
      concatMap(() =>
        snapshot.signature === this.lastSavedProjectSignature
          ? of(undefined)
          : this.updateTabloProjectGQL
              .mutate({ variables: snapshot.variables })
              .pipe(first()),
      ),
      tap(() => {
        if (
          this.fullProject$.value?.tabloProjectId ===
          snapshot.entry.tabloProjectId
        ) {
          // Acknowledgement belongs to this exact payload, even if the user has
          // edited the project again while the request was running.
          this.lastSavedProjectSignature = snapshot.signature;
          this.lastSavedHistoryEntryId$.next(
            snapshot.entry.lastHistoryEntryId ?? null,
          );
          this.saveError$.next(false);
        }
      }),
      catchError((error: unknown) => {
        this.saveError$.next(true);
        return throwError(error);
      }),
    );
  }

  hasUnsavedChanges(): boolean {
    const current = this.fullProject$.value;
    if (!current) {
      return false;
    }
    return (
      this.createAutosavePayloadSignature(current) !==
      this.lastSavedProjectSignature
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload($event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      $event.preventDefault();
      $event.returnValue = '';
    }
  }

  async saveProject(): Promise<void> {
    const entry = this.fullProject$.value;
    if (!entry) {
      return;
    }
    const snapshot = this.createSaveSnapshot(entry);
    if (!this.projectSaveQueue) {
      this.projectSaveQueue = new TabloProjectSaveQueue((request) =>
        this.persistProject(request),
      );
    }
    this.pendingSaveCount++;
    this.saveInProgress$.next(true);
    try {
      await this.projectSaveQueue.save(snapshot);
    } finally {
      this.pendingSaveCount--;
      this.saveInProgress$.next(this.pendingSaveCount > 0);
    }
  }

  async retrySave(): Promise<void> {
    try {
      await this.saveProject();
    } catch {
      this.saveError$.next(true);
    }
  }

  async saveAndExit(): Promise<void> {
    try {
      await this.saveProject();
      if (!this.hasUnsavedChanges()) {
        this.exited = true;
        this.exitRequested.emit();
      }
    } catch {
      this.saveError$.next(true);
    }
  }

  trackTextName(index, item) {
    return item.type;
  }

  canvasLoaded() {}

  scrollZoomUpdated($event: { scale: number; x: number; y: number }) {
    if ($event.scale === this.minScaleValue.value) {
      this.x$.next(0);
      this.y$.next(0);
      this.scaleControl.patchValue(this.minScaleValue.value);
    } else {
      this.scaleControl.patchValue($event.scale);
      this.x$.next($event.x);
      this.y$.next($event.y);
    }
  }

  async addStaticText() {
    const font = await this.fontsManagerService.pickFont(
      'Wybierz czcionkę dla tego pola',
    );

    this.addToCurrentPage({
      type: 'statictext',
      config: {
        y: 500,
        x: 800,
        transform: 'translate(500px, 500px)',
        width: 700,
        height: 300,
        index: null,
        rotation: 0,
        config: {
          text: `Tutaj wpisz swój tekst`,
          fontSize: 50,
          align: 'left',
          verticalAlign: 'top',
          font: {
            fontId: font.fontId,
            fileName: font.fileName,
          },
          index: null,
          headerFont: {
            fileName: font.fileName,
            fontId: font.fontId,
          },
          headerAlign: 'left',
          headerColor: '#000000',
          headerFontSize: 50,
          color: '#000000',
          headerFontLatterSpacing: 0,
          fontLatterSpacing: 0,
          padding: 0,
          borderRadius: 0,
          background: 'rgba(255, 255, 255, 0)',
        },
      },
    } as TabloStructureElementForAdd<TabloStaticText>);
  }
  async addQr() {
    if (this.isSinglePageTablo) {
      return;
    }

    this.addToCurrentPage({
      type: 'qr',
      config: {
        y: 500,
        x: 800,
        transform: 'translate(500px, 500px)',
        width: 94,
        height: 94,
        index: null,
        rotation: 0,
      },
    } as TabloStructureElementForAdd<TabloQrConfigHost>);
  }

  photosAssigned(matches: Record<string, TabloFaceToPerson>) {
    if (matches === null) {
      return;
    }
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        if (!draft) {
          return draft;
        }
        draft.lastHistoryEntryId = cuid();
        draft.faceMatchConfig = {
          ...draft.faceMatchConfig,
          ...matches,
        };
      }),
    );
    this.templateControl.setValue(this.fullProject$.value);
  }

  removeStaticText(name: any) {
    this.removeItem(name.id);
  }

  trackStaticText(i: number, text) {
    return text.id;
  }

  async addGraphic($event: { target: HTMLInputElement }) {
    const file = $event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const imageSize = await ImageMetadataStatic.getImageDimensions(file);
      if (!imageSize.width || !imageSize.height) {
        return;
      }

      const fileName = await firstValueFrom(
        this.uploadService.uploadSingleFile(
          file,
          this.uploadContextForAssets,
          this.projectId,
        ),
      );

      this.addToCurrentPage({
        type: 'graphic',
        config: {
          y: 500,
          x: 500,
          transform: 'translate(500px, 500px)',
          source: 'graphic',
          width: imageSize.width,
          height: imageSize.height,
          fileName,
          index: null,
          hidden: false,
          config: {
            opacity: 1,
          },
        },
      } as TabloStructureElementForAdd<TabloCustomGraphic>);
    } catch (error) {
      if (!isUnsupportedJpegColorSpaceError(error)) {
        console.error(error);
      }
    } finally {
      $event.target.value = '';
    }
  }

  removeItem(id: string) {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // a) new history entry

        const page = draft.pages[this.currentPage$.value]; // b) current page
        page.structure = reIndexStructure(
          // c) filter + re‑index
          page.structure.filter((item) => item.id !== id),
        );
      }),
    );
  }

  async addPhotographerLogo() {
    const currentLogo = await firstValueFrom(this.logo$);
    this.addToCurrentPage({
      id: cuid(),
      type: 'photographerLogo',
      editMode: false,
      config: {
        y: 500,
        x: 500,
        transform: 'translate(500px, 500px)',
        fileName: currentLogo,
        height: Math.round(this.fullProject$.value.height * 0.15),
        width: Math.round(this.fullProject$.value.height * 0.15),
        config: {
          opacity: 1,
        },
      },
    } as TabloLogoElement);
  }

  addSchoolPhoto() {
    this.addToCurrentPage({
      id: cuid(),
      type: 'photoOfSchool',
      editMode: false,
      config: {
        index: null,
        y: 500,
        x: 500,
        source: 0,
        transform: 'translate(500px, 500px)',
        width: Math.round(this.fullProject$.value.width * 0.5),
        height: Math.round(this.fullProject$.value.height * 0.5),
        hidden: false,
        config: {
          sideMargin: 0,
          photoAsCircle: false,
          photoAsSquare: false,
          childTilt: 0,
          childPhotoFrameWidth: 0,
          childPhotoFrameColor: 'rgb(255, 255, 255)',
          childPhotoOverlayFileName: null,
          childPhotoOverlayScale: 1,

          childPhotoShadow: null,
          childRadius: 0,
          photoId: null,
          photoGroupId: null,
          fileName: null,
          x: 0,
          y: 0,
          scale: 1,
          schoolPhotoNumber: 0,
          width: 0,
          height: 0,
        },
      },
    } as TabloPhotoOfSchool);
  }

  addToCurrentPage(
    element: Omit<TabloStructoreElement, 'id' | 'isOnHover' | 'editMode'> & {
      id?: string;
      isOnHover?: boolean;
      editMode?: boolean;
    },
  ) {
    const pageIdx = this.currentPage$.value;

    const newElement: TabloStructoreElement = {
      ...element,
      id: cuid(),
      editMode: false,
      isOnHover: false,
      config: {
        ...element.config,
        /* index is “next free slot” in the current page */
        index: this.fullProject$.value.pages[pageIdx].structure.length,
      },
    } as TabloStructoreElement;

    const nextState = updateState(this.fullProject$.value, (draft) => {
      draft.lastHistoryEntryId = cuid();
      draft.pages[pageIdx].structure.push(newElement);
    });

    /* 3️⃣  Emit the fresh snapshot */
    this.fullProject$.next(nextState);
  }

  addChildStaticPhoto() {
    this.addToCurrentPage({
      id: cuid(),
      type: 'staticChildPhoto',
      editMode: false,
      config: {
        index: null,
        y: 500,
        x: 500,
        source: 0,
        transform: 'translate(500px,500px)',
        width: Math.round(this.fullProject$.value.width * 0.5),
        height: Math.round(this.fullProject$.value.height * 0.5),
        hidden: false,
        config: {
          sideMargin: 0,
          photoAsCircle: false,
          photoAsSquare: false,
          childTilt: 0,
          childPhotoFrameWidth: 0,
          childPhotoFrameColor: 'rgb(255, 255, 255)',
          childPhotoOverlayFileName: null,
          childPhotoShadow: null,
          childRadius: 0,
          childPhotoOverlayScale: 1,
          scale: 1,
          photoGroupId: null,
          photoId: null,
          y: 0,
          x: 0,
          schoolPhotoNumber: 0,
          width: 0,
          height: 0,
          fileName: null,
        },
      },
    });
  }

  async dropLayer(event: CdkDragDrop<string[]>): Promise<void> {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // create a history entry

        const page = draft.pages[this.currentPage$.value];

        // 1. Make sure the structure has sequential indices first
        const structure = reIndexStructure(page.structure);

        // 2. Put it in UI order (index 0 == top of the stack)
        const ordered = [...structure].sort(
          (a, b) => b.config.index - a.config.index, // descending => top first
        );

        // 3. Move the dragged layer within that order
        moveItemInArray(ordered, event.previousIndex, event.currentIndex);

        // 4. Re-assign indices so they are gap-free and bottom-up
        ordered.forEach((item, uiIdx) => {
          item.config.index = ordered.length - 1 - uiIdx; // 0 == bottom, max == top
        });

        // 5. Persist back to the page
        page.structure = ordered;
      }),
    );
  }

  toggleElementVisibility(id: string, hidden: boolean): void {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // ➊ new history entry

        const pageIdx = this.currentPage$.value; // ➋ work only on current page
        const page = draft.pages[pageIdx];

        /* ➌ find the element and mutate its draft‑copy in place */
        const element = page.structure.find((el) => el.id === id);
        if (element) {
          // ensure the array has the latest contiguous indices first
          element.config.hidden = hidden;
        }
      }),
    );
  }

  toggleElementLock(id: string, b: boolean) {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // ➊ new history entry

        const pageIdx = this.currentPage$.value; // ➋ work only on current page
        const page = draft.pages[pageIdx];

        /* ➌ find the element and mutate its draft‑copy in place */
        const element = page.structure.find((el) => el.id === id);
        if (element) {
          // ensure the array has the latest contiguous indices first
          element.config.locked = b;
        }
      }),
    );
  }

  async showErrors() {
    this.hideControls.next(true);
    await firstValueFrom(
      this.zalamoDialogService.openFull(this.errorsExplained).afterClosed(),
    );
    this.hideControls.next(false);
  }

  overflowError({
    page: pageIndex,
    id,
    hasError,
  }: {
    page: number;
    id: string;
    hasError: boolean;
  }): void {
    const project = this.fullProject$.value;
    if (!project) {
      return;
    }

    const page = project.pages[pageIndex];
    const element = page?.structure.find(
      (item): item is TabloStaticText =>
        item.id === id && item.type === 'statictext',
    );

    if (!element || element.overflowError === hasError) {
      return;
    }

    this.fullProject$.next(
      updateState(project, (draft) => {
        // draft.lastHistoryEntryId = cuid(); // ➊ new history entry

        const page = draft.pages[pageIndex]; // ➋ only event source page
        if (!page) {
          return;
        }

        const element = page.structure.find(
          (el) => el.id === id && el.type === 'statictext',
        ) as Draft<TabloStaticText> | undefined;

        if (element) {
          element.overflowError = hasError;
        }
      }),
    );
  }

  showDump() {
    this.zalamoDialogService.openFull(this.pageDump);
  }

  editCrop($event: TabloMember) {
    this.currentMemberForCrop = $event.tabloMemberId;
    if (
      this.sidebarNavigation.snapshot.current.panel !== 'group-edit' &&
      this.currentGroupId
    ) {
      this.sidebarNavigation.openRoot('group-edit', {
        structureId: this.currentGroupId,
      });
    }
    this.sidebarNavigation.push('face-match', {
      memberId: $event.tabloMemberId,
      structureId: this.currentGroupId ?? undefined,
    });
  }

  faceMatchUpdated($event: {
    x: number;
    y: number;
    scale: number;
    rotation: number;
  }) {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        const member = draft.faceMatchConfig[
          this.currentMemberForCrop
        ] as TabloFaceToPerson;
        if (member) {
          draft.lastHistoryEntryId = cuid(); // ➊ new history entry
          // Immer lets us assign directly
          draft.faceMatchConfig = {
            ...draft.faceMatchConfig,
            [this.currentMemberForCrop]: {
              ...member,
              x: $event.x,
              y: $event.y,
              scale: $event.scale,
              rotation: $event.rotation,
            },
          };
        }
      }),
    );
    this.templateControl.setValue(this.fullProject$.value);
  }

  closeFaceMatch() {
    this.currentMemberForCrop = null;
    this.sidebarNavigation.back();
  }

  saveSingleChildConfig() {
    const v = this.singleChildForm.getRawValue();
    const idx = v.childIdx;
    delete v.childIdx;

    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid();
        const page = draft.pages[this.currentPage$.value];
        const group = page.structure.find(
          (el): el is TabloGroupElementConfig =>
            el.type === 'group' && el.id === this.currentChildGroupId,
        );
        if (!group) {
          return;
        }

        if (!group.config.config.singleChildConfig) {
          group.config.config.singleChildConfig = {};
        }
        /** scal z istniejącą konfiguracją aby nic nie nadpisać przypadkiem */
        group.config.config.singleChildConfig[idx] = {
          ...group.config.config.singleChildConfig[idx],
          ...v,
        };
      }),
    );
  }

  editSingleChild(group: TabloGroupElementConfig, idx: number) {
    /** prepare the list for the selector */
    this.currentGroupChildren = group.config.list.members.map((m, i) => ({
      idx: i,
      name: `${m.firstName} ${m.lastName}`,
    }));
    this.currentGroupId = group.id;
    this.currentChildGroupId = group.id;
    this.currentChildIdx = idx;

    /** preload existing overrides (if any) */
    const overrides =
      group.config.config.singleChildConfig?.[idx] ??
      ({} as Partial<TabloGroupConfig['singleChildConfig']>);
    this.singleChildForm.patchValue(
      {
        childIdx: idx,
        ...overrides,
      },
      { emitEvent: false },
    );

    if (this.sidebarNavigation.snapshot.current.panel !== 'group-edit') {
      this.sidebarNavigation.openRoot('group-edit', {
        structureId: group.id,
        groupId: group.config.list.id,
      });
    }

    this.sidebarNavigation.push('single-child-edit', {
      structureId: group.id,
      groupId: group.config.list.id,
      childIndex: idx,
    });
    this.singleChildEditSubscription?.unsubscribe();
    this.singleChildEditSubscription = this.singleChildForm.valueChanges
      .pipe(tap(() => this.saveSingleChildConfig()))
      .subscribe();
  }

  pointerOverLayer(layer: { id: string }) {
    document.getElementById(layer.id)?.classList.add('hoverFromLayers');
  }

  pointerLeftLayer(layer: { id: string }) {
    document.getElementById(layer.id)?.classList.remove('hoverFromLayers');
  }

  duplicateGraphic(id: string) {
    const pageIdx = this.currentPage$.value;
    const page = this.fullProject$.value.pages[pageIdx];
    const graphic = page.structure.find((el) => el.id === id);
    if (graphic) {
      this.addToCurrentPage({
        ...graphic,

        config: {
          ...graphic.config,
          index: page.structure.length,
        },
      });
    }
  }

  async undo(): Promise<void> {
    const current = this.fullProject$.value;
    if (!current) return;
    try {
      const entry = this.prepareProjectForPersistence(current);
      const signature = this.createAutosavePayloadSignature(entry);
      const previous = await this.projectHistory.getPrevious(entry);
      this.historyError$.next(false);
      const latest = this.fullProject$.value;
      if (
        previous &&
        latest &&
        this.createAutosavePayloadSignature(latest) === signature
      ) {
        this.isRedoAvailable$.next(true);
        this.fullProject$.next({ ...previous, fromHistory: true });
        this.templateControl.setValue(previous);
      }
    } catch {
      this.historyError$.next(true);
    }
  }

  async redo(): Promise<void> {
    const current = this.fullProject$.value;
    if (!current) return;
    try {
      const entry = this.prepareProjectForPersistence(current);
      const signature = this.createAutosavePayloadSignature(entry);
      const next = await this.projectHistory.getNext(entry);
      this.historyError$.next(false);
      const latest = this.fullProject$.value;
      if (!latest || this.createAutosavePayloadSignature(latest) !== signature)
        return;
      if (!next) {
        this.isRedoAvailable$.next(false);
        return;
      }
      this.fullProject$.next({ ...next, fromHistory: true });
      this.templateControl.setValue(next);
    } catch {
      this.historyError$.next(true);
    }
  }

  editStaticPhoto(item: TabloStructoreElement) {
    switch (item.type) {
      case 'photoOfSchool':
        this.currentStaticPhotoType = 'school';
        break;
      case 'staticChildPhoto':
        this.currentStaticPhotoType = 'child';
        break;
    }
    // @ts-ignore
    this.currentPhotoConfig = item.config.config;
    this.currentGroupId = item.id;
    this.patchEditMode(item.id, true);
    this.sidebarNavigation.openRoot('photo-edit', {
      structureId: item.id,
      photoType: this.currentStaticPhotoType,
    });
  }
  editStaticGraphic(item: TabloStructoreElement) {
    // @ts-ignore
    this.currentGraphicConfig = item.config.config;
    this.currentGroupId = item.id;
    this.patchEditMode(item.id, true);
    this.sidebarNavigation.openRoot('graphic-edit', {
      structureId: item.id,
    });
  }

  staticGraphicUpdated($event: StaticGraphicConfig) {
    this.fullProject$.next(
      updateState(this.fullProject$.value, (draft) => {
        draft.lastHistoryEntryId = cuid(); // 1️⃣ history

        const currentPage = draft.pages[this.currentPage$.value];

        const element = currentPage.structure.find(
          (el) => el.id === this.currentGroupId,
        );

        if (element) {
          (element.config as unknown as TabloCustomGraphic).config = {
            ...(element.config as unknown as TabloCustomGraphic).config,
            ...$event, // merge update
          };
        }
      }),
    );
  }
  trackAttendance(index: number, item: { value: string }) {
    return item.value;
  }

  async addTeacher() {
    const { PublicSessionOrganizationsModule } =
      await import('../../../../../../photographer-panel/src/app/modules/public-session-organizations/public-session-organizations.module');
    const injector = createInjector(
      PublicSessionOrganizationsModule,
      this.injector,
    );
    const carouselModule = injector.get(PublicSessionOrganizationsModule);
    carouselModule.editTeacherRoles(
      this.publicSessionOrganizationId,
      this.publicSessionOrganizationGroupId,
      this.contextId,
    );
  }
  async editAllTeachers() {
    const { PublicSessionOrganizationsModule } =
      await import('../../../../../../photographer-panel/src/app/modules/public-session-organizations/public-session-organizations.module');
    const injector = createInjector(
      PublicSessionOrganizationsModule,
      this.injector,
    );
    const carouselModule = injector.get(PublicSessionOrganizationsModule);
    carouselModule.editTeachers(
      this.publicSessionOrganizationId,
      this.contextId,
    );
  }
}

const reIndexStructure = (
  structure: TabloProjectStructure,
): TabloProjectStructure => {
  return structure
    .sort((a, b) => {
      if (a.config.index === undefined) {
        a.config.index = structure.indexOf(a);
      }
      if (b.config.index === undefined) {
        b.config.index = structure.indexOf(b);
      }
      if (a.config.index === b.config.index) {
        return 0;
      }
      return a.config.index - b.config.index;
    })
    .map((item, index) => ({
      ...item,
      config: {
        ...(item.config as any),
        index,
      },
    }));
};
